"use client";

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { PastePanel } from "./PastePanel";
import { SheetPanel } from "./SheetPanel";
import {
  buildCheckoutReturnToast,
  checkoutReturnSessionId,
  stripCheckoutReturnParams,
  type CheckoutResumeResponse,
} from "./checkoutReturn";
import {
  analysisFromDraft,
  rememberResumeDraft,
  restoreDraftId,
  resumeDraftId,
  stripResumeDraftParam,
  takeRememberedResumeDraft,
} from "./resumeDraft";
import { buildPaywallAuthHref, resumePaywallLeadId, stripResumePaywallParam } from "./paywallResume";
import { resolveSaveAttempt } from "./saveAttempt";
import type { Map as MLMap } from "maplibre-gl";
import {
  AppShell,
  Badge,
  Button,
  Card,
  Download,
  Drawer,
  Ellipsis,
  Icon,
  IconButton,
  Input,
  Label,
  Modal,
  Pencil,
  RotateCcw,
  Save,
  Share2,
  ShellMain,
  SlidersHorizontal,
  Tabs,
  FrozenMapNotice,
  Toast,
  ToastRegion,
  Topbar,
  Paywall,
  useProUpsell,
  type ToastVariant,
  type ToastAction,
  type UpsellPrice,
  type PaywallCopyVariant,
} from "@/components";
import type { QuotaLimit } from "@/lib/billing/quota";
import type { AnalysisToolFlags } from "@/config/brand.schema";
import {
  anyToolPresent,
  buildLassoTerritory,
  buildRadiusCircle,
  emptyAnalysis,
  territoryColor,
  type AnalysisEntitlement,
} from "@/lib/analysis";
import {
  analyzeGrid,
  assembleGeocodeRequest,
  buildRows,
  diffGeocodeRequests,
  loadDraft,
  makeDraft,
  parseDelimited,
  parseFile,
  remediateRow,
  renderedSpecFromDraft,
  rowKeyForAddress,
  saveDraft,
  DEFAULT_COUNTRY,
  HARD_ROW_CEILING,
  type ColumnMapping,
  type ColumnRole,
  type GeocodeRequestDTO,
  type IngestAnalysis,
  type IngestSource,
  type IngestSourceKind,
  type ParseGrid,
  type ParsedRow,
} from "@/lib/ingest";
import {
  addPointToSet,
  applyManualEdits,
  buildPointSet,
  canAddPoint,
  coordinateModeResolver,
  defaultSpec,
  geocodeResultResolver,
  placeLabel,
  removePointFromSet,
  synthesizePoint,
  type MapSpec,
  type Point,
  type PointSet,
  type TileDescriptor,
} from "@/lib/map";
import type { GeocodeBatchResponse, GeocodeResult } from "@/lib/contracts/geocode";
import type { ShareVisibility } from "@/lib/contracts/share";
// Client-safe share engine — buildSnapshot is pure; the server repo lives
// behind "@/lib/share/server" and never enters this bundle.
import { buildSnapshot, pointSetFromSnapshot, type MapSnapshot } from "@/lib/share";
import { AI_ASSIST_ENABLED } from "@/config/features";
// Browser-safe deep imports — the "@/lib/nl" barrel re-exports server-only
// modules (pg-backed logging) that must never enter the client bundle.
import { redo as redoNL, reduceStack } from "@/lib/nl/opstack";
import { buildSavedView } from "@/lib/nl/savedView";
import { shouldAutoMakeMap } from "./autoMake";
import type { NLApplyResult } from "@/lib/nl/client";
import type { MapOp, SavedView } from "@/lib/contracts/nl";
import { clientTrack } from "@/lib/analytics/client";
import { createFunnelEvents } from "@/lib/analytics/events";
import { firstTouchToAppOpenedProps, readFirstTouch } from "@/lib/analytics/firstTouch";
import { emitCheckoutFailed, quotaLimitToQuotaType, quotaLimitToTrigger } from "@/lib/analytics/paywall";
import { buildMapRenderedFirstProps, claimFirstMapForIdentity } from "./firstMap";
import { IntakeSurface, type IntakeLoading } from "./IntakeSurface";
import { MatchPanel } from "./MatchPanel";
import { MapPlate } from "./MapPlate";
import { PlacesPanel, type PlacePreview } from "./PlacesPanel";
import { NLBar, type SaveViewRequest } from "./NLBar";
import { StylePanel } from "../map/StylePanel";
import { BasemapPanel } from "../map/BasemapPanel";
import { AnalysisPanel, type AnalysisDrawRequest } from "../map/AnalysisPanel";
import { OverlayPanel } from "../map/OverlayPanel";
import { getMapTokens } from "@/lib/map";
import type { MapView, OverlayRenderData } from "../map/MapCanvas";
import { overlayGate, type OverlayEntitlement } from "@/lib/overlay/gate";
import { overlayCatalog } from "@/lib/overlay/adapters";
import { resolveOverlayData } from "@/lib/overlay/resolve";
import { geoJoin } from "@/lib/overlay/geoJoin";
import type { OverlayEngine } from "@/lib/overlay/engine";
import { ShareModal, type ShareInfo } from "../share/ShareModal";
import { ExportDialog } from "../share/ExportDialog";
import { LandingApi, LandingAsk, LandingCta, LandingHero, LandingSteps, LandingSync } from "../landing/sections";
import { SiteFooter } from "@/components/site-footer";
import { SiteHeader, BrandLockup } from "@/components/site-chrome";
import { AppNav } from "@/components/app-nav";
import { Breadcrumb } from "@/components/breadcrumb";
import { sourceDisplayName } from "./labels";
import "./ingest.css";
import "./intake-panels.css";

function defaultMapTitle(source: IngestSource): string {
  if (source.kind === "paste") return "Untitled map";
  return sourceDisplayName(source).replace(/\.(csv|tsv|xlsx?|txt)$/i, "");
}

/**
 * A saved map handed to the editor for editable rehydration (FAIL-506). The
 * server loads it when a signed-in owner opens `/?map=<id>` for a live, saved,
 * owned map; the editor mounts straight into the rendered state from the
 * snapshot (no re-ingest, no re-geocode) so styling/settings edits + re-save
 * work in place. `share` is present when the map has a live share config, so
 * "Save" re-publishes the existing map rather than minting a new one.
 */
export interface EditorInitialMap {
  id: string;
  name: string;
  snapshot: MapSnapshot;
  share: ShareInfo | null;
}

export interface IngestEditorProps {
  /** Global-nav auth state (from getCurrentAccount on the server); false = public nav. */
  signedIn?: boolean;
  /** White-label brand values (server-resolved via getTheme) — never literals. */
  brandName: string;
  /** Public API base URL (`https://<appDomain>/v1`) — feeds the landing's API
   *  code sample so the developer section stays literal-free (white-label). */
  apiBaseUrl: string;
  /** Analytics identity (anon session id) for the M5 NL funnel events. */
  anonSessionId: string;
  wordmark: { text: string; logoUrl: string | null };
  /** The square brand mark (`brand.logoUrl`) shown next to the wordmark in the
   *  landing footer's brand column — see SiteFooter's `markUrl` doc. Optional:
   *  omitted just renders the wordmark alone. */
  markUrl?: string;
  /** Active theme id — resolves the canvas paint tokens client-side. */
  themeName: string;
  /** Vendor-clean tile descriptor (server-resolved via @/lib/map/server). */
  tile: TileDescriptor;
  /** Esri satellite descriptor (MAP-102). */
  satelliteTile?: TileDescriptor;
  /**
   * Landing-footer legal strings (server-resolved via getTheme) — the no-data
   * state renders the marketing landing around the intake, and its footer
   * needs these. Optional: without it the footer trims to brand + attribution.
   */
  landingLegal?: {
    legalEntity: string;
    supportEmail: string;
    tosUrl: string;
    privacyUrl: string;
    cookiesUrl?: string;
    dpaUrl?: string;
    acceptableUseUrl?: string;
    supportUrl?: string;
  };
  /**
   * Paid plan for the soft-block upsell (M7 FR-Q5): the tier id checkout targets
   * plus the transparent price (server-resolved from the tunable plan table).
   * Null when no paid plan is configured — the modal then omits the price line
   * and the CTA still routes to checkout (which degrades gracefully).
   */
  upsellPlan?: { tier: string; price: UpsellPrice; mapPointLimit: number | null } | null;
  /**
   * Anonymous point cap from the server-loaded free plan. This lets Save show
   * the real map-points paywall before the auth redirect; the API repeats the
   * same check and remains authoritative.
   */
  freeMapPointLimit?: number | null;
  /**
   * Frozen-map read-only state (M7 Flow D). When an OWNER opens a map frozen by
   * a downgrade, the editor renders a read-only overlay + one-click Resubscribe.
   * Set by the (pending) owner map-load path; until that auth/load vehicle
   * lands this stays null and the overlay never shows. The public path already
   * 404s a frozen map (Phase 2) — this is only the owner's in-editor state.
   */
  frozenMap?: { title?: string } | null;
  /**
   * Editable rehydration of a saved map (FAIL-506). When present, the editor
   * opens with this map already rendered from its snapshot — ready to restyle,
   * adjust settings, and re-save. Absent for a normal cold open / create flow.
   */
  initialMap?: EditorInitialMap | null;
  /**
   * AskBar hand-off (FAIL-440) — the question typed into the dashboard/detail
   * AskBar, routed here as `?prompt=`. Threaded straight to the NL bar, which
   * seeds and focuses its input once the dataset is live. Absent for a normal
   * cold open.
   */
  initialPrompt?: string;
  /**
   * AskBar/launcher deep-link (FAIL-458) — `?intake=paste|sheet` from the signed-in
   * /maps/new surface. Opens + focuses the matching intake method on the no-data
   * landing. Absent for a normal cold open (the drop bar is the primary path).
   */
  initialIntake?: "paste" | "sheet";
  /**
   * Signed-in create mode (FAIL-458). When true, the no-data state renders a
   * clean in-app intake (drop / paste / sheet + an AI prompt that seeds the NL
   * bar) instead of the public marketing landing — so `/maps/new` hosts the real
   * create flow in the app shell and becomes the editor once data loads. The
   * has-data editor is unchanged.
   */
  appMode?: boolean;
  /** Header content (title + quota) shown above the appMode intake; server-
   *  rendered by the create page so it can read the owner's quota. */
  appIntakeHeader?: ReactNode;
  /**
   * M8 deep-analysis — per-tool build-go flags (white-label config) + the tier-
   * derived entitlement. Both default to "off / unentitled" so the wedge and any
   * caller that omits them ship with the analysis panel fully absent.
   */
  analysisTools?: AnalysisToolFlags;
  analysisEntitlement?: AnalysisEntitlement;
  /** Pro/Pro Plus entitlement for Ask your map. */
  aiAssistAllowed?: boolean;
  /** Pro-tier data-export capability. Lite is paid but cannot export rows. */
  dataExportAllowed?: boolean;
  /**
   * M9 demographics/census overlays — the build-go flag + the tier-derived
   * entitlement (both default off/unentitled so the wedge ships with the overlay
   * panel fully absent). `overlayEngine` is the deploy-time provider seam: absent
   * in the wedge (no DB), so `spec.overlay` persists + the panel authors it, but
   * no choropleth resolves until a deployment binds a provider-backed engine.
   */
  demographicsOverlays?: boolean;
  overlayEntitlement?: OverlayEntitlement;
  overlayEngine?: OverlayEngine;
  /**
   * FAIL-474 — "Link a Google Sheet" is Pro-gated on the create surface (both
   * creating from a sheet AND keeping it in sync are paid). Paid users get the
   * first-class SheetPanel; free users get a locked card → /pricing. Defaults to
   * unentitled so the homepage/wedge never exposes the live-sheet method freely.
   */
  sheetEntitlement?: { isPaid: boolean };
  /**
   * MAP-102 follow-up — the tier-derived entitlement for paid basemaps
   * (satellite). Free by default so the wedge/homepage never exposes satellite;
   * the Maps-tab tile locks it and routes to the Pro upsell for free accounts.
   * Server-authoritative enforcement is the save-time clamp in the mint route.
   */
  basemapEntitlement?: { isPaid: boolean };
}

const ALL_TOOLS_OFF: AnalysisToolFlags = {
  radius: false,
  territoriesLasso: false,
  territoriesAutomated: false,
  territoriesBoundary: false,
  driveTime: false,
  routes: false,
};

interface ToastItem {
  id: number;
  variant: ToastVariant;
  title: string;
  message?: string;
  /** Optional inline action (e.g. Undo on a reversible edit). */
  action?: ToastAction;
}

function sourceKindForFile(name: string): IngestSourceKind {
  const lower = name.toLowerCase();
  if (lower.endsWith(".tsv")) return "tsv";
  if (lower.endsWith(".xlsx")) return "xlsx";
  if (lower.endsWith(".xls")) return "xls";
  return "csv";
}

function analyticsSource(source: IngestSource): "paste" | "upload" | "sheets" {
  if (source.kind === "paste") return "paste";
  if (source.kind === "sheets_url") return "sheets";
  return "upload";
}

function localDraftId(): string {
  const uuid = globalThis.crypto?.randomUUID?.() ?? Date.now().toString(36);
  return `draft_local_${uuid}`;
}

/**
 * FAIL-474 free-tier presentation of the Google Sheet method: a locked card that
 * names the Pro feature (live, in-sync data) and routes to /pricing. The method
 * stays visible (so free users see the wedge) but isn't usable until they upgrade.
 */
function SheetLockedCard() {
  return (
    <div className="pz-sheet-locked">
      <p className="pz-sheet-locked__title">
        Live Google Sheets
        <Badge variant="accent">Pro</Badge>
      </p>
      <p className="pz-sheet-locked__body">
        Link a sheet and your map keeps itself in sync as the data changes. Upgrade to connect live data.
      </p>
      <Link className="pz-sheet-locked__cta" href="/pricing/">
        See Pro plans
      </Link>
    </div>
  );
}

function AiLockedCard({ onUpgrade }: { onUpgrade: () => void }) {
  return (
    <div className="pz-sheet-locked">
      <p className="pz-sheet-locked__title">
        Ask your map
        <Badge variant="accent">Pro</Badge>
      </p>
      <p className="pz-sheet-locked__body">Use plain language to explore your data and update the map.</p>
      <Button variant="upgrade" size="sm" onClick={onUpgrade}>See Pro plans</Button>
    </div>
  );
}

/**
 * The M3 ingest editor — one client surface holding the whole flow: intake
 * (file / paste / public Google Sheet) → auto-detected column match → the
 * map-hero layout with the match panel and attention tray beside the framed
 * plate → "Make map", which assembles the GeocodeRequest DTO (the only
 * payload that crosses to M2) and persists the resumable draft.
 */
export function IngestEditor({
  signedIn = false,
  brandName,
  apiBaseUrl,
  anonSessionId,
  wordmark,
  markUrl,
  themeName,
  tile,
  satelliteTile,
  landingLegal,
  upsellPlan,
  freeMapPointLimit,
  frozenMap,
  initialMap,
  initialPrompt,
  initialIntake,
  appMode = false,
  appIntakeHeader,
  analysisTools = ALL_TOOLS_OFF,
  analysisEntitlement = { isPaid: false },
  aiAssistAllowed = false,
  dataExportAllowed = false,
  demographicsOverlays = false,
  overlayEntitlement = { isPaid: false },
  overlayEngine,
  sheetEntitlement = { isPaid: false },
  basemapEntitlement = { isPaid: false },
}: IngestEditorProps) {
  // D7 (MAP-123): ShareModal's visibility listbox opens the generic Pro modal
  // client-side (gated selections never reach the API); this is the ONE
  // defensive fallback for a stale client that still POSTs a gated value —
  // see handleSetVisibility's 402 branch below. Requires the (editor) route
  // group's <ProModalMount> (mirrors D2's account-side mount) to be an
  // ancestor of this component — see (editor)/layout.tsx.
  const { openProUpsell } = useProUpsell();
  const [analysis, setAnalysis] = useState<IngestAnalysis | null>(null);
  const [source, setSource] = useState<IngestSource | null>(null);
  const [mapping, setMapping] = useState<ColumnMapping | null>(null);
  const [rows, setRows] = useState<ParsedRow[]>([]);
  const [overridden, setOverridden] = useState<ReadonlySet<ColumnRole>>(new Set());
  const [excluded, setExcluded] = useState<ReadonlySet<number>>(new Set());
  // Rehydration (FAIL-506): a saved map opened via `/?map=<id>` seeds the editor
  // straight into its rendered state — id (so re-save targets it), the PointSet
  // rebuilt from the snapshot, the published spec, its share config, and its
  // saved views. Cold opens keep the empty defaults. Since `live` derives from
  // `pointSet && spec`, seeding those two is what puts the map on the plate.
  const [draftId, setDraftId] = useState<string | null>(initialMap?.id ?? null);
  const [loading, setLoading] = useState<IntakeLoading>(null);
  const [intakeError, setIntakeError] = useState<string | null>(null);
  const [lastRequest, setLastRequest] = useState<GeocodeRequestDTO | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const toastSeq = useRef(0);
  const router = useRouter();

  // M4 — the rendered artifact: the PointSet (resolved coordinates) + the
  // MapSpec (the whole declarative style). Style edits rewrite the spec; the
  // canvas diffs it onto the live map with zero reloads.
  const [pointSet, setPointSet] = useState<PointSet | null>(() =>
    initialMap ? pointSetFromSnapshot(initialMap.snapshot) : null,
  );
  const [spec, setSpec] = useState<MapSpec | null>(initialMap?.snapshot.spec ?? null);
  const [mappingPreview, setMappingPreview] = useState<{
    pointSet: PointSet;
    spec: MapSpec;
    label: string;
  } | null>(null);
  const mappingPreviewRequest = useRef(0);
  const [geocoding, setGeocoding] = useState(false);
  // MAP-128 — the last "Make map" attempt resolved zero placeable points. Drives
  // an inline reason ON the map plate (above the fold) so an empty result isn't
  // explained only by a 6s toast + the attention tray below the fold in the side
  // column. Cleared whenever a fresh attempt starts, the mapping changes, or the
  // dataset is reset; presentMap sets the definitive value each make.
  const [placementFailed, setPlacementFailed] = useState(false);
  // "Start over" wipes the whole in-progress map (unrecoverable for an unsaved
  // build), so the toolbar button opens the app's standard confirmation Modal —
  // the same explicit Confirm/Cancel dialog the dashboard delete uses — rather
  // than a bespoke inline two-click confirm.
  const [startOverOpen, setStartOverOpen] = useState(false);
  const [mapName, setMapName] = useState(initialMap?.name ?? "Untitled map");
  const [mapNameConfirmed, setMapNameConfirmed] = useState(Boolean(initialMap));
  const [namePromptOpen, setNamePromptOpen] = useState(false);
  const [namePromptValue, setNamePromptValue] = useState(initialMap?.name ?? "Untitled map");
  const [namePromptError, setNamePromptError] = useState<string | null>(null);
  const [renamingMap, setRenamingMap] = useState(false);
  const [inlineName, setInlineName] = useState(initialMap?.name ?? "Untitled map");
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  /** Armed by applyGrid when the auto-detected mapping is confident enough to
   *  draw the map without a manual "Make map" click; consumed once by the effect
   *  below. A ref (not state) so arming it never itself triggers a render. */
  const autoMakePendingRef = useRef(false);
  /** Signed-in /maps/new builds persist as soon as their first rendered map is
   *  ready. Consumed once by the effect below so rerenders and later edits do
   *  not create duplicates. */
  const autoSaveNewMapRef = useRef(false);
  const landingFileInputRef = useRef<HTMLInputElement>(null);
  /** Geocode results by row_key — re-makes reuse coords, never re-geocode unchanged rows. */
  const geocodeCache = useRef<Map<string, GeocodeResult>>(new Map());
  // MAP-7 — point-level edits kept as an overlay so they survive an "Update map"
  // re-geocode (applyManualEdits re-applies them onto the rebuilt PointSet).
  // `manualAdded` are the hand-added points; `removedIds` are pins removed by
  // hand. Both are refs (not state): the live PointSet state already re-renders
  // the list, and these only feed the re-make reconciliation.
  const manualAddedRef = useRef<Point[]>([]);
  const removedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!overflowOpen) return;
    function closeOverflow(event: MouseEvent) {
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) setOverflowOpen(false);
    }
    function closeOverflowOnEscape(event: KeyboardEvent) {
      if (event.key === "Escape") setOverflowOpen(false);
    }
    document.addEventListener("mousedown", closeOverflow);
    document.addEventListener("keydown", closeOverflowOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOverflow);
      document.removeEventListener("keydown", closeOverflowOnEscape);
    };
  }, [overflowOpen]);

  // M6 — share & export. The minted share (slug/urls) lives for the current
  // dataset; the live MapLibre map (via the MapCanvas onReady seam) is the
  // PNG-capture source. Republishing to the SAME slug has no endpoint yet, so
  // one dataset mints once and Start over clears it.
  const [share, setShare] = useState<ShareInfo | null>(initialMap?.share ?? null);
  const hasCommittedShareRef = useRef(Boolean(initialMap?.share));
  const saveInFlightRef = useRef(false);
  const mintInFlightRef = useRef(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [minting, setMinting] = useState(false);
  const [mintError, setMintError] = useState<string | null>(null);
  const liveMapRef = useRef<MLMap | null>(null);
  // A map-specific `?prompt=` deep link prefills the NL bar once the map is live.
  const promptSeed = initialPrompt;

  // M7 — soft-block upsell. A quota block (HTTP 409 quota_exceeded) names the
  // limit + carries the upsell; we open a modal instead of a generic error.
  const [upsell, setUpsell] = useState<
    { limit: QuotaLimit; action: string; usage: number; copyVariant: PaywallCopyVariant } | null
  >(null);
  const [upgrading, setUpgrading] = useState(false);

  // M5 — the NL layer over the rendered map. The GUI-authored spec is the
  // BASE; NL STYLE ops stack on top (rendered spec = reduceStack(nlBase, nlOps),
  // computed by the NL client glue). `nlBase` is null while the stack is empty
  // — the plain `spec` is then both base and rendered. QUERY highlights are
  // transient row ids threaded down to the canvas overlay.
  const [nlOps, setNlOps] = useState<MapOp[]>([]);
  /** Ops removed by Undo, ordered so Redo can replay them without a model call. */
  const [nlRedoOps, setNlRedoOps] = useState<MapOp[]>([]);
  const [nlBase, setNlBase] = useState<MapSpec | null>(null);
  const [nlHighlights, setNlHighlights] = useState<string[]>([]);
  /** QUERY on-map annotation badges (group-by aggregates / distance-to). */
  const [nlAnnotations, setNlAnnotations] = useState<Array<{ id: string; text: string }>>([]);
  /** QUERY `fit` effect — ids + a nonce so the same match set re-frames. */
  const [nlFit, setNlFit] = useState<{ ids: string[]; nonce: number } | null>(null);
  /** Promoted saved views (AC-10) — published into the M6 snapshot. */
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => initialMap?.snapshot.saved_views ?? []);
  /** Stable map id for NL requests when no server draft id exists yet. */
  const [nlFallbackId] = useState(() => localDraftId());
  /** When the last NL op landed — powers the undo-within-10s misinterpretation signal. */
  const nlAppliedAt = useRef(0);

  // The client analytics funnel — the validator-wired browser tracker (shared
  // with the paywall/quota/NL emitters), so client-side taxonomy drift is caught.
  const analytics = useMemo(() => createFunnelEvents(clientTrack), []);
  const analyticsCtx = useMemo(() => ({ anonSessionId, sourceSurface: "editor" as const }), [anonSessionId]);
  const intakeSurface = appMode ? "new_map" as const : "homepage" as const;

  // §6 Stage A — top of funnel: the app was opened (once per mount).
  const appOpenedFired = useRef(false);
  useEffect(() => {
    if (appOpenedFired.current) return;
    appOpenedFired.current = true;
    analytics.appOpened(analyticsCtx, firstTouchToAppOpenedProps(readFirstTouch()));
  }, [analytics, analyticsCtx]);

  const intakeViewedFired = useRef(false);
  useEffect(() => {
    if (initialMap || intakeViewedFired.current) return;
    intakeViewedFired.current = true;
    analytics.intakeViewed(analyticsCtx, { intake_surface: intakeSurface });
  }, [analytics, analyticsCtx, initialMap, intakeSurface]);

  // FAIL-484: the Stripe-hosted checkout redirects here as `/?checkout=success
  // &session_id=...` with NOTHING previously reading either param — a paying
  // customer landed on a plain homepage with zero confirmation, and the
  // resume endpoint built for exactly this (bridges the webhook race) was
  // never called. Runs once per mount; cleans the URL immediately so a
  // refresh/back-nav never re-fires it. Decision logic lives in
  // checkoutReturn.ts (pure, unit-tested) — this effect is glue only.
  const checkoutReturnHandled = useRef(false);
  useEffect(() => {
    if (checkoutReturnHandled.current || typeof window === "undefined") return;
    const sessionId = checkoutReturnSessionId(window.location.search);
    checkoutReturnHandled.current = true;
    if (!sessionId) return;

    const query = stripCheckoutReturnParams(window.location.search);
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);

    void (async () => {
      let data: CheckoutResumeResponse | null = null;
      try {
        const res = await fetch("/api/billing/resume", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });
        data = res.ok ? ((await res.json()) as CheckoutResumeResponse) : null;
      } catch {
        data = null;
      }
      pushToast(buildCheckoutReturnToast(data));
      if (data?.paid) {
        // The webhook (the only path that writes billing_state) almost always
        // lands well within this delay — refreshing re-renders the server
        // props (sheetEntitlement/analysisEntitlement/overlayEntitlement etc.)
        // with the real, now-paid entitlement, which is what actually clears
        // whatever was gated. Deliberately not attempting to fake individual
        // client-side entitlement states for every possible gated action.
        window.setTimeout(() => router.refresh(), 1500);
      }
    })();
    // Runs once on mount by design (checkoutReturnHandled is the real guard);
    // pushToast/router are stable across the component's lifetime for this purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Anonymous paywall continuation: Clerk returned with an identified account.
  // getCurrentAccount inside the checkout route performs the anon-session claim
  // before resolving this lead, so Stripe can only start for the real user.
  const paywallResumeHandled = useRef(false);
  useEffect(() => {
    if (paywallResumeHandled.current || typeof window === "undefined" || !signedIn) return;
    const leadId = resumePaywallLeadId(window.location.search);
    paywallResumeHandled.current = true;
    if (!leadId) return;

    const query = stripResumePaywallParam(window.location.search);
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);
    void resumeCapturedPaywallCheckout(leadId);
    // The URL token is the exactly-once guard. The helper is a hoisted function.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  // FAIL-485: restore the IndexedDB draft after authentication and complete the
  // interrupted Save. The query string is the primary continuation; tab-scoped
  // sessionStorage is the fallback because some SSO callbacks discard Clerk's
  // post-auth query string and return to `/`.
  const resumeDraftHandled = useRef(false);
  const resumeAutoSaveRef = useRef(false);
  useEffect(() => {
    if (resumeDraftHandled.current || typeof window === "undefined") return;
    const queryAutoSaveId = resumeDraftId(window.location.search);
    let rememberedAutoSaveId: string | null = null;
    if (signedIn) {
      try {
        rememberedAutoSaveId = takeRememberedResumeDraft(window.sessionStorage);
      } catch {
        // sessionStorage may be unavailable; the URL continuation still works.
      }
    }
    const autoSaveId = queryAutoSaveId ?? rememberedAutoSaveId;
    const id = autoSaveId ?? restoreDraftId(window.location.search);
    if (!id) {
      // Authentication may still be hydrating. Keep the effect armed until the
      // signed-in state arrives so it can consume the tab-scoped continuation.
      if (signedIn) resumeDraftHandled.current = true;
      return;
    }
    resumeDraftHandled.current = true;

    const query = stripResumeDraftParam(window.location.search);
    window.history.replaceState(null, "", query ? `?${query}` : window.location.pathname);

    void (async () => {
      const draft = await loadDraft(id).catch(() => null);
      if (!draft) {
        pushToast({
          variant: "warning",
          title: "Couldn't recover your map",
          message: "The draft wasn't found on this device/browser — paste or upload your data again to rebuild it.",
        });
        return;
      }
      setSource(draft.source);
      setAnalysis(analysisFromDraft(draft));
      setMapping(draft.mapping);
      setRows(draft.rows);
      setDraftId(draft.draft_id);
      const restoredName = draft.title?.trim() || defaultMapTitle(draft.source);
      setMapName(restoredName);
      setInlineName(restoredName);
      setNamePromptValue(restoredName);
      setMapNameConfirmed(Boolean(draft.title_confirmed));
      setSpec(renderedSpecFromDraft(draft)); // null is fine — presentMap only reuses a truthy prev spec
      autoMakePendingRef.current = true;
      resumeAutoSaveRef.current = Boolean(autoSaveId);
    })();
    // Runs once on mount by design (resumeDraftHandled is the real guard).
  }, [signedIn]);

  // Completes the interrupted Save once the resumed draft's map has actually
  // regenerated (pointSet/spec committed) and the sign-in redirect has landed
  // the user back signed in. Disarms immediately so it fires exactly once.
  useEffect(() => {
    if (!resumeAutoSaveRef.current || !pointSet || !spec || !signedIn) return;
    resumeAutoSaveRef.current = false;
    void handleSave("resume");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointSet, spec, signedIn]);

  // Confidence-gated auto-generate: when applyGrid armed the ref for a trustworthy
  // mapping, draw the map as soon as the upload's state has committed — no manual
  // "Make map" click. The ref is disarmed on the same tick so it fires exactly
  // once per upload; later mapping edits re-run this effect with the ref already
  // false and never re-trigger it.
  useEffect(() => {
    if (!autoMakePendingRef.current || !analysis || !mapping || !source) return;
    autoMakePendingRef.current = false;
    void handleMakeMap();
    // Keyed off the committed upload state; handleMakeMap is a stable hoisted
    // declaration and the ref is the real one-shot guard, so both are omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysis, mapping, source]);

  // A generated map on the signed-in create page is account data, not a
  // disposable draft. Save it privately on first render; the normal Save
  // action remains available for subsequent edits.
  useEffect(() => {
    if (!autoSaveNewMapRef.current || !pointSet || !spec || !signedIn) return;
    autoSaveNewMapRef.current = false;
    void performSave(mapName, "automatic");
    // performSave is a hoisted declaration; the ref is the one-shot guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointSet, spec, signedIn, mapName]);

  // §6 Stage B north-star (`map_rendered_first`): the clock starts at editor
  // mount and the event fires ONCE, at the REAL first paint (pointSet+spec
  // committed), with the true marker_count / style / geocode_strategy — never on
  // mount with a placeholder. `firstMapFired` dedupes within the session; the
  // localStorage claim dedupes across reloads for the same identity.
  const mountedAt = useRef(typeof performance !== "undefined" ? performance.now() : 0);
  const firstMapFired = useRef(false);
  const lastGeocodeRef = useRef<{ mode: "coordinate" | "address"; results: readonly GeocodeResult[] }>({
    mode: "coordinate",
    results: [],
  });
  useEffect(() => {
    if (firstMapFired.current || !pointSet || !spec) return;
    firstMapFired.current = true;
    const nowMs = typeof performance !== "undefined" ? performance.now() : 0;
    const props = buildMapRenderedFirstProps({
      mapId: draftId ?? nlFallbackId,
      markerCount: pointSet.count,
      spec,
      mode: lastGeocodeRef.current.mode,
      results: lastGeocodeRef.current.results,
      elapsedMs: nowMs - mountedAt.current,
    });
    if (claimFirstMapForIdentity(anonSessionId)) {
      analytics.mapRenderedFirst(analyticsCtx, props);
    }
  }, [pointSet, spec, analytics, analyticsCtx, draftId, nlFallbackId, anonSessionId]);

  /** Raw data cells (post-header), the stable input to every re-validation. */
  const dataCells = useMemo(() => (analysis ? analysis.rows.map((r) => r.cells) : []), [analysis]);

  /**
   * Ready / attention / skipped counts with user exclusions applied. `skipped`
   * (blank + summary rows) is kept apart from `attention` so it never inflates
   * the "N need attention" count the user is asked to act on.
   */
  const effectiveSummary = useMemo(() => {
    let ready = 0;
    let attention = 0;
    let skipped = 0;
    rows.forEach((row, index) => {
      if (excluded.has(index)) return;
      if (row.status === "ready") ready += 1;
      else if (row.status === "skipped") skipped += 1;
      else attention += 1;
    });
    return { ready, attention, skipped };
  }, [rows, excluded]);

  function pushToast(toast: Omit<ToastItem, "id">) {
    const id = ++toastSeq.current;
    setToasts((prev) => [...prev, { id, ...toast }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 6000);
  }

  /** Mint a server draft id (fall back to a local one offline) + persist. */
  async function establishDraft(a: IngestAnalysis, s: IngestSource) {
    let id = localDraftId();
    try {
      const res = await fetch("/api/ingest/draft", { method: "POST" });
      if (res.ok) {
        const body = (await res.json()) as { draft_id?: string };
        if (typeof body.draft_id === "string" && body.draft_id !== "") id = body.draft_id;
      }
    } catch {
      // Offline — the local id keeps on-device resume working.
    }
    setDraftId(id);
    try {
      await saveDraft(
        makeDraft({
          draft_id: id,
          title: defaultMapTitle(s),
          source: s,
          profiles: a.profiles,
          mapping: a.mapping,
          rows: a.rows,
          country_default: a.countryDefault,
        }),
      );
    } catch {
      // IndexedDB unavailable — analysis still works, resume doesn't. Non-fatal.
    }
  }

  function applyGrid(grid: ParseGrid, s: IngestSource) {
    if (grid.rows.length < 2) {
      throw new Error("That looks empty — we need a header row plus at least one data row.");
    }
    const a = analyzeGrid(grid);
    setAnalysis(a);
    setSource(s);
    const nextMapName = defaultMapTitle(s);
    setMapName(nextMapName);
    setInlineName(nextMapName);
    setNamePromptValue(nextMapName);
    setMapNameConfirmed(false);
    setNamePromptOpen(false);
    setNamePromptError(null);
    setRenamingMap(false);
    setMapping(a.mapping);
    setRows(a.rows);
    setOverridden(new Set());
    setExcluded(new Set());
    setLastRequest(null);
    setDraftId(null);
    setPanelOpen(false);
    setPointSet(null);
    setSpec(null);
    mappingPreviewRequest.current += 1;
    setMappingPreview(null);
    setGeocoding(false);
    setPlacementFailed(false);
    resetShareState();
    resetNLState();
    setSavedViews([]);
    geocodeCache.current.clear();
    manualAddedRef.current = [];
    removedIdsRef.current = new Set();
    // §6 Stage A — the auto-detected mapping was shown (friction-killer quality signal).
    analytics.columnMappingProposed(analyticsCtx, {
      column_count: a.header.length,
      address_column_count: Object.keys(a.mapping.roles).length,
    });
    // Skip the manual "Make map" click when detection is confident (see
    // shouldAutoMakeMap). Arm the ref here; the effect fires once state has
    // committed, so handleMakeMap reads the freshly-set analysis/mapping/rows.
    autoMakePendingRef.current = shouldAutoMakeMap(a.mapping, a.rows);
    autoSaveNewMapRef.current = appMode && signedIn;
    void establishDraft(a, s);
  }

  async function handleFile(file: File) {
    setLoading("file");
    setIntakeError(null);
    // §6 Stage A — ingest entry: the time-to-first-map clock starts here.
    analytics.importStarted(analyticsCtx, { source: "upload", intake_surface: intakeSurface });
    try {
      const grid = await parseFile(file);
      applyGrid(grid, { kind: sourceKindForFile(file.name), filename: file.name, sizeBytes: file.size });
    } catch (err) {
      analytics.importFailed(analyticsCtx, {
        failure_code: "parse_error",
        source_kind: "upload",
        source: "upload",
        intake_surface: intakeSurface,
      });
      setIntakeError(err instanceof Error ? err.message : "That file couldn't be read. Try exporting it as CSV.");
    } finally {
      setLoading(null);
    }
  }

  function handlePaste(text: string) {
    setLoading("paste");
    setIntakeError(null);
    analytics.importStarted(analyticsCtx, { source: "paste", intake_surface: intakeSurface });
    try {
      applyGrid(parseDelimited(text), { kind: "paste" });
    } catch (err) {
      analytics.importFailed(analyticsCtx, {
        failure_code: "parse_error",
        source_kind: "paste",
        source: "paste",
        intake_surface: intakeSurface,
      });
      setIntakeError(err instanceof Error ? err.message : "Those rows couldn't be read. Paste them with the header row included.");
    } finally {
      setLoading(null);
    }
  }

  async function handleSheets(url: string) {
    setLoading("sheets");
    setIntakeError(null);
    analytics.importStarted(analyticsCtx, { source: "sheets", intake_surface: intakeSurface });
    try {
      const res = await fetch("/api/ingest/sheets", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const body = (await res.json().catch(() => null)) as { csv?: string; error?: string } | null;
      if (!res.ok || typeof body?.csv !== "string") {
        analytics.importFailed(analyticsCtx, {
          failure_code: "fetch_failed",
          source_kind: "sheets",
          source: "sheets",
          intake_surface: intakeSurface,
        });
        setIntakeError(body?.error ?? "Could not read that Google Sheet. Download it as CSV and drop it here.");
        return;
      }
      applyGrid(parseDelimited(body.csv), { kind: "sheets_url", url });
    } catch (err) {
      analytics.importFailed(analyticsCtx, {
        failure_code: "parse_error",
        source_kind: "sheets",
        source: "sheets",
        intake_surface: intakeSurface,
      });
      setIntakeError(err instanceof Error ? err.message : "Could not reach that Google Sheet.");
    } finally {
      setLoading(null);
    }
  }

  /**
   * FAIL-474 first-class sheet: the SheetPanel already fetched + previewed the
   * CSV, so this just applies it (with the sheet source recorded) — the live
   * binding is registered later, on save, once the map has a persisted dataset.
   */
  function handleSheetCsv({ csv, url }: { csv: string; url: string }) {
    setIntakeError(null);
    analytics.importStarted(analyticsCtx, { source: "sheets", intake_surface: intakeSurface });
    try {
      applyGrid(parseDelimited(csv), { kind: "sheets_url", url });
    } catch (err) {
      analytics.importFailed(analyticsCtx, {
        failure_code: "parse_error",
        source_kind: "sheets",
        source: "sheets",
        intake_surface: intakeSurface,
      });
      setIntakeError(err instanceof Error ? err.message : "Could not read that Google Sheet.");
    }
  }

  /**
   * Register the linked Google Sheet as a LIVE source (FAIL-474) after the map is
   * saved and owns a dataset. Fire-and-forget from the save path: the binding
   * does a live Google fetch + first sync, so it must never block the Save toast.
   * Daily auto-sync is the wedge — a paid-only cadence the create surface gates.
   */
  async function registerSheetBinding(datasetId: string, sheetUrl: string) {
    try {
      const res = await fetch("/api/sync/bindings", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ dataset_id: datasetId, sheet_url: sheetUrl, refresh_policy: "daily" }),
      });
      if (res.ok) {
        pushToast({ variant: "success", title: "Live sync on", message: "This map refreshes daily from your sheet." });
      } else {
        // The map IS saved either way; the binding is best-effort. Keep the copy
        // neutral — the cause could be a transient fetch, not a sharing problem.
        pushToast({
          variant: "info",
          title: "Saved — live sync didn’t start",
          message: "Your map is saved; you can re-link the sheet from its map later.",
        });
      }
    } catch {
      pushToast({
        variant: "info",
        title: "Saved — live sync didn’t start",
        message: "Your map is saved; you can re-link the sheet from its map later.",
      });
    }
  }

  /** Resolve one real row so the user can visually verify a manual mapping. */
  async function previewMapping(next: ColumnMapping, nextRows: ParsedRow[]) {
    const requestId = ++mappingPreviewRequest.current;
    setMappingPreview(null);
    if (!analysis) return;
    const sample = nextRows.find((row, index) => row.status === "ready" && !excluded.has(index));
    if (!sample) return;

    let previewSet: PointSet;
    if (next.mode === "coordinate") {
      previewSet = buildPointSet(analysis.profiles, [sample], coordinateModeResolver(next));
    } else {
      const request = assembleGeocodeRequest({
        dataset_id: draftId ?? nlFallbackId,
        mapping: next,
        rows: [sample],
        country_default: analysis.countryDefault,
      });
      const requestRow = request.rows[0];
      if (!requestRow) return;
      let result = geocodeCache.current.get(requestRow.row_key);
      if (!result) {
        try {
          const res = await fetch("/api/geocode", {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify(request),
          });
          if (!res.ok) return;
          const body = (await res.json()) as GeocodeBatchResponse;
          result = body.results?.[0];
          if (result) geocodeCache.current.set(result.row_key, result);
        } catch {
          return;
        }
      }
      if (!result) return;
      previewSet = buildPointSet(analysis.profiles, [sample], geocodeResultResolver([result]));
    }

    if (requestId !== mappingPreviewRequest.current || previewSet.count === 0) return;
    const labelColumn = next.label_columns[0] ?? 0;
    const label = sample.cells[labelColumn]?.trim() || "First spreadsheet row";
    setMappingPreview({
      pointSet: previewSet,
      spec: defaultSpec(previewSet, { theme: themeName }),
      label,
    });
  }

  /** Apply a mapping change and RE-RUN row validation over the raw cells. */
  function applyMapping(next: ColumnMapping) {
    const nextRows = buildRows(next, dataCells);
    setMapping(next);
    setRows(nextRows);
    // Editing the match invalidates the prior attempt's "No rows could be
    // placed" plate state — reverts to the neutral "press Make map" prompt.
    setPlacementFailed(false);
    void previewMapping(next, nextRows);
  }

  function handleModeChange(mode: ColumnMapping["mode"]) {
    if (!mapping || mapping.mode === mode) return;
    applyMapping({ ...mapping, mode });
  }

  function handleRoleChange(role: ColumnRole, column: number | null) {
    if (!mapping) return;
    const roles = { ...mapping.roles };
    if (column === null) delete roles[role];
    else roles[role] = column;
    setOverridden((prev) => new Set(prev).add(role));
    // §6 Stage A — user overrode the auto-detected mapping (inverse auto-detect signal).
    analytics.columnMappingCorrected(analyticsCtx, {
      columns_corrected_count: overridden.has(role) ? overridden.size : overridden.size + 1,
    });
    applyMapping({ ...mapping, roles });
  }

  function handleToggleLabel(column: number) {
    if (!mapping) return;
    const has = mapping.label_columns.includes(column);
    const label_columns = has
      ? mapping.label_columns.filter((i) => i !== column)
      : [...mapping.label_columns, column].sort((a, b) => a - b);
    // Labels don't affect geocoding, so no re-validation is needed.
    setMapping({ ...mapping, label_columns });
  }

  function handleReset() {
    if (!analysis) return;
    setMapping(analysis.mapping);
    setRows(analysis.rows);
    setOverridden(new Set());
  }

  function handleRemoveBrokenRow(index: number) {
    const hasRemainingMapRow = rows.some(
      (row, rowIndex) =>
        rowIndex !== index && !excluded.has(rowIndex) && row.status !== "skipped",
    );

    if (!hasRemainingMapRow) {
      handleStartOver();
      return;
    }

    setExcluded((prev) => {
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }

  function handleRemediateRow(
    index: number,
    edits: ReadonlyArray<{ column: number; value: string }>,
  ) {
    if (!analysis || !mapping || !source) return;
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index ? remediateRow(mapping, row, edits) : row,
    );
    setRows(nextRows);
    setPlacementFailed(false);
    const id = draftId ?? nlFallbackId;
    void saveDraft(
      makeDraft({
        draft_id: id,
        title: mapName,
        title_confirmed: mapNameConfirmed,
        source,
        profiles: analysis.profiles,
        mapping,
        rows: nextRows,
        country_default: analysis.countryDefault,
        spec: nlBase ?? spec ?? undefined,
        nl_ops: nlOps,
        saved_views: savedViews,
      }),
    ).catch(() => {
      // IndexedDB unavailable — the in-memory edit remains usable this session.
    });
  }

  /**
   * A resolved PointSet arrived (either path) — reconcile the MapSpec and put
   * the map on the plate. An existing spec survives a re-make as long as every
   * column it references still exists (style edits are never thrown away);
   * otherwise the zero-config default takes over.
   */
  function presentMap(ps: PointSet, reusedCount: number | null) {
    // MAP-7: re-apply this session's point-level edits onto the freshly rebuilt
    // set, so hand-added/removed points survive an "Update map" re-geocode.
    const merged = applyManualEdits(ps, manualAddedRef.current, removedIdsRef.current);
    if (merged.count === 0) {
      // MAP-128 — persist the reason on the plate; the toast alone auto-dismisses.
      setPlacementFailed(true);
      pushToast({
        variant: "warning",
        title: "No rows could be placed",
        message: "Check the column match — none of the ready rows produced a usable coordinate.",
      });
      return;
    }
    setPlacementFailed(false);
    mappingPreviewRequest.current += 1;
    setMappingPreview(null);
    // Re-making the map commits any NL styling into the surviving spec (the
    // stillValid path below keeps it) and starts a fresh NL history — old
    // highlights + saved views reference row ids that may no longer exist.
    resetNLState();
    setSavedViews([]);
    setPointSet(merged);
    // §6 Stage A — the dataset committed after mapping (second Activation-funnel step).
    if (source) {
      analytics.importCommitted(analyticsCtx, {
        dataset_id: draftId ?? nlFallbackId,
        row_count: ps.count,
        source: analyticsSource(source),
        intake_surface: intakeSurface,
      });
    }
    setSpec((prev) => {
      if (prev) {
        const has = (col?: string) => col === undefined || merged.columns.some((c) => c.name === col);
        const stillValid =
          has(prev.encoding.colorBy?.column) &&
          has(prev.encoding.sizeBy?.column) &&
          has(prev.heat.weightColumn) &&
          has(prev.popup.titleColumn) &&
          prev.popup.fields.every((f) => has(f)) &&
          prev.filters.every((f) => has(f.column));
        if (stillValid) return prev;
      }
      const next = defaultSpec(merged, { theme: themeName });
      // Honor M3's marker-label picks as the popup fields — minus the title
      // column (it already heads the popup); an empty remainder keeps the
      // zero-config default fields.
      const labelNames = (mapping?.label_columns ?? [])
        .map((i) => analysis?.profiles[i]?.header)
        .filter((h): h is string => typeof h === "string" && h !== "" && h !== next.popup.titleColumn);
      if (labelNames.length > 0) {
        next.popup = { ...next.popup, fields: labelNames.slice(0, 6) };
      }
      return next;
    });
    pushToast({
      variant: "success",
      title: `${merged.count.toLocaleString()} points on the map`,
      message:
        reusedCount !== null && reusedCount > 0
          ? `${reusedCount.toLocaleString()} coordinates reused — only new rows were geocoded.`
          : undefined,
    });
  }

  async function handleMakeMap() {
    if (!analysis || !mapping || !source || geocoding) return;
    // A fresh attempt clears any prior "No rows could be placed" plate state;
    // presentMap re-sets it if this attempt also lands zero points (MAP-128).
    setPlacementFailed(false);
    const datasetId = draftId ?? localDraftId();
    const excludedKeys: string[] = [];
    for (const index of excluded) {
      const key = rows[index]?.row_key;
      if (key) excludedKeys.push(key);
    }
    const request = assembleGeocodeRequest({
      dataset_id: datasetId,
      mapping,
      rows,
      country_default: analysis.countryDefault,
      excluded: excludedKeys,
    });
    const diff = lastRequest ? diffGeocodeRequests(lastRequest, request) : null;

    void saveDraft(
      makeDraft({
        draft_id: datasetId,
        title: mapName,
        title_confirmed: mapNameConfirmed,
        source,
        profiles: analysis.profiles,
        mapping,
        rows,
        country_default: analysis.countryDefault,
        // Carry any surviving NL state through a re-make so resume restores the fold.
        spec: nlBase ?? spec ?? undefined,
        nl_ops: nlOps,
        saved_views: savedViews,
      }),
    ).catch(() => {
      // IndexedDB unavailable — the request DTO is still produced below.
    });

    setLastRequest(request);
    const activeRows = rows.filter((_, index) => !excluded.has(index));

    if (mapping.mode === "coordinate") {
      // Coordinates come straight off the rows — render immediately, no network.
      lastGeocodeRef.current = { mode: "coordinate", results: [] };
      presentMap(buildPointSet(analysis.profiles, activeRows, coordinateModeResolver(mapping)), null);
      return;
    }

    // Address mode: the assembled DTO IS the geocode request. Rows already in
    // the cache are never re-sent (the diff/cache reuse criterion).
    setGeocoding(true);
    try {
      const cache = geocodeCache.current;
      const missing = request.rows.filter((row) => !cache.has(row.row_key));
      if (missing.length > 0) {
        const res = await fetch("/api/geocode", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ ...request, rows: missing }),
        });
        if (!res.ok) throw new Error(`geocode failed (${res.status})`);
        const body = (await res.json()) as GeocodeBatchResponse;
        if (!Array.isArray(body?.results)) throw new Error("geocode returned no results");
        for (const result of body.results) cache.set(result.row_key, result);
      }
      const results = request.rows
        .map((row) => cache.get(row.row_key))
        .filter((r): r is GeocodeResult => r !== undefined);
      const reused = request.rows.length - missing.length;
      lastGeocodeRef.current = { mode: "address", results };
      presentMap(buildPointSet(analysis.profiles, activeRows, geocodeResultResolver(results)), reused);
      if (diff && missing.length > 0) {
        // The DTO diff is the audit trail; the cache above enforces reuse.
        console.debug("[ingest] geocode diff", {
          unchanged: diff.unchanged.length,
          changed: diff.changed.length,
          sent: missing.length,
        });
      }
    } catch {
      pushToast({
        variant: "danger",
        title: "Geocoding is unavailable right now",
        message: "Nothing was lost — try again in a moment, or switch to coordinate mode.",
      });
    } finally {
      setGeocoding(false);
    }
  }

  function handleStartOver() {
    setAnalysis(null);
    setSource(null);
    setMapping(null);
    setRows([]);
    setOverridden(new Set());
    setExcluded(new Set());
    setDraftId(null);
    setIntakeError(null);
    setLastRequest(null);
    setPanelOpen(false);
    setPointSet(null);
    setSpec(null);
    mappingPreviewRequest.current += 1;
    setMappingPreview(null);
    setGeocoding(false);
    setPlacementFailed(false);
    setMapName("Untitled map");
    setInlineName("Untitled map");
    setNamePromptValue("Untitled map");
    setMapNameConfirmed(false);
    setNamePromptOpen(false);
    setNamePromptError(null);
    setRenamingMap(false);
    setOverflowOpen(false);
    resetShareState();
    resetNLState();
    setSavedViews([]);
    geocodeCache.current.clear();
    manualAddedRef.current = [];
    removedIdsRef.current = new Set();
  }

  /** Map pans/zooms fold back into the spec (fit turns off — the view is now the user's). */
  function handleViewChange(view: MapView) {
    const fold = (prev: MapSpec | null) =>
      prev ? { ...prev, view: { center: view.center, zoom: view.zoom, fit: false } } : prev;
    setSpec(fold);
    // The NL base mirrors the user's view too, so Undo/Reset never jump the camera.
    setNlBase(fold);
  }

  /* -------------------------------------------------------- MAP-7 · points */

  /**
   * Geocode one typed address through the SAME provider chain the batch path
   * uses (/api/geocode — vendor-neutral, free budget/caching). Returns a preview
   * candidate to confirm, or null when nothing matched. The result is also
   * seeded into the geocode cache keyed by the row's identity, so a later
   * "Update map" re-make reuses it instead of re-billing.
   */
  async function handleFindPlace(rawQuery: string): Promise<PlacePreview | null> {
    const q = rawQuery.trim();
    if (q === "") return null;
    const rowKey = rowKeyForAddress(q);
    const res = await fetch("/api/geocode", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        schema_version: 1,
        dataset_id: draftId ?? nlFallbackId,
        mode: "address",
        rows: [{ row_key: rowKey, query: q }],
        country_default: analysis?.countryDefault ?? DEFAULT_COUNTRY,
      }),
    });
    if (!res.ok) throw new Error(`geocode failed (${res.status})`);
    const body = (await res.json()) as GeocodeBatchResponse;
    const result = body.results?.[0];
    if (!result || result.lat === null || result.lng === null) return null;
    geocodeCache.current.set(result.row_key, result);
    return {
      query: q,
      label: result.normalized_input || q,
      lat: result.lat,
      lng: result.lng,
      confidence: result.confidence,
      needsReview: result.needs_review,
    };
  }

  /**
   * Append a previewed place as a REAL dataset row: the typed text lands in the
   * popup title column (or the first column), every other column empty, and the
   * geocoded geometry becomes the pin. It joins the in-memory PointSet the map
   * renders and Save serializes, so it survives Save + appears on the shared map
   * with no parallel model. Enforces the row ceiling and de-dupes by row_key.
   */
  function handleAddPlace(preview: PlacePreview) {
    if (!pointSet || !spec) return;
    const guard = canAddPoint(pointSet.count, HARD_ROW_CEILING);
    if (!guard.ok) {
      pushToast({ variant: "warning", title: "Point limit reached", message: guard.message });
      return;
    }
    const id = rowKeyForAddress(preview.query);
    if (pointSet.points.some((p) => p.id === id)) {
      pushToast({ variant: "info", title: "Already on the map", message: "That place is already a point on this map." });
      return;
    }
    const addressColumn = spec.popup.titleColumn ?? pointSet.columns[0]?.name ?? "Place";
    const pt = synthesizePoint({
      query: preview.query,
      lat: preview.lat,
      lng: preview.lng,
      confidence: preview.confidence,
      columns: pointSet.columns,
      addressColumn,
    });
    setPointSet((prev) => (prev ? addPointToSet(prev, pt) : prev));
    manualAddedRef.current = [...manualAddedRef.current, pt];
    removedIdsRef.current.delete(pt.id);
    pushToast({ variant: "success", title: "Place added", message: `${preview.label} is now on the map.` });
  }

  /**
   * Remove one pin from the map (the point-level counterpart to add). Drops the
   * row from the live PointSet and offers an inline Undo before the change is
   * committed on Save. A hand-added point is pulled from the add overlay; an
   * imported point is recorded in `removedIds` so a re-make keeps it removed.
   */
  function handleRemovePlace(id: string) {
    const removed = pointSet?.points.find((p) => p.id === id);
    if (!removed) return;
    setPointSet((prev) => (prev ? removePointFromSet(prev, id) : prev));
    const wasManual = manualAddedRef.current.some((p) => p.id === id);
    if (wasManual) manualAddedRef.current = manualAddedRef.current.filter((p) => p.id !== id);
    else removedIdsRef.current.add(id);
    const label = placeLabel(removed, spec?.popup.titleColumn);
    pushToast({
      variant: "success",
      title: "Place removed",
      message: `${label} was removed.`,
      action: {
        label: "Undo",
        onClick: () => {
          setPointSet((prev) => (prev ? addPointToSet(prev, removed) : prev));
          if (wasManual) manualAddedRef.current = [...manualAddedRef.current, removed];
          else removedIdsRef.current.delete(id);
        },
      },
    });
  }

  /* -------------------------------------------------------- M6 · share */

  /** User-facing title shown in the editor and persisted with the map. */
  const mapTitle = mapName;

  function replaceShare(nextShare: ShareInfo | null) {
    hasCommittedShareRef.current = nextShare !== null;
    setShare(nextShare);
  }

  function resetShareState() {
    replaceShare(null);
    setShareOpen(false);
    setExportOpen(false);
    setMinting(false);
    setMintError(null);
    liveMapRef.current = null;
  }

  /**
   * Mint the share: build the snapshot from the CURRENT rendered state (spec
   * with NL ops folded in, coordinates pre-resolved — the viewer never
   * geocodes) and POST it. Attribution is re-stamped server-side from the
   * white-label theme, so it is non-removable by construction.
   */
  async function mintShare(
    intent: "save" | "publish" = "publish",
    titleOverride?: string,
    saveSource: "automatic" | "resume" | "manual" | "publish" = intent === "publish" ? "publish" : "manual",
  ): Promise<boolean> {
    // Save and Share both reach this create path. Guard it synchronously because
    // React's `minting` state does not update until the next render, leaving a
    // brief window where two callbacks could otherwise mint two maps.
    if (!pointSet || !spec || minting || mintInFlightRef.current || hasCommittedShareRef.current) return false;
    mintInFlightRef.current = true;
    setMinting(true);
    setMintError(null);
    try {
      const snapshot = buildSnapshot(pointSet, spec, {
        title: titleOverride ?? mapTitle,
        attribution: tile.attribution,
        pinnedView: spec.view.fit ? null : { center: spec.view.center, zoom: spec.view.zoom },
        savedViews,
      });
      const res = await fetch("/api/maps", {
        method: "POST",
        headers: { "content-type": "application/json" },
        // The real op stack rides along (persisted on maps.nl_op_stack), and the
        // saved views serialize inside the snapshot — the viewer replays both
        // with zero LLM calls (AC-2, AC-10).
        // MAP-118: send the new free-tier default (`public`) explicitly rather
        // than the old `unlisted` literal — `unlisted` is now Pro-gated
        // server-side, so a hardcoded `unlisted` here would 402 every free
        // user's first publish. The visibility control (ShareModal) lets the
        // owner change it afterward, subject to the same gate.
        body: JSON.stringify({
          snapshot,
          intent,
          save_attempt_id: crypto.randomUUID(),
          save_source: saveSource,
          visibility: intent === "save" ? "private" : "public",
          column_schema: pointSet.columns,
          nl_op_stack: nlOps,
        }),
      });
      const body = (await res.json().catch(() => null)) as {
        share_slug?: string;
        url?: string;
        embed_url?: string;
        dataset_id?: string;
        visibility?: ShareVisibility;
        message?: string;
        status?: string;
        limit_hit?: QuotaLimit | null;
        upsell?: { limit: QuotaLimit; action: string } | null;
        usage?: number;
        copy_variant?: PaywallCopyVariant;
      } | null;
      // M7 soft-block: a structured quota block opens the paywall (naming the
      // limit + linking to checkout), never a generic publish error. M12-B: the
      // server resolved the paywall_copy variant + its exposure at the gate; the
      // paywall renders the assigned copy and fires the canonical paywall_hit.
      if (res.status === 409 && body?.status === "quota_exceeded" && body.upsell) {
        setShareOpen(false);
        setUpsell({
          limit: body.upsell.limit,
          action: body.upsell.action,
          usage: body.usage ?? 0,
          copyVariant: body.copy_variant ?? "control",
        });
        return false;
      }
      if (!res.ok || !body?.share_slug || !body.url || !body.embed_url) {
        throw new Error(`${intent === "save" ? "Saving" : "Publishing"} failed — try again in a moment.`);
      }
      const committedShare: ShareInfo = {
        slug: body.share_slug,
        url: body.url,
        embedUrl: body.embed_url,
        visibility: body.visibility ?? "public",
        viewersCanExport: false,
        lastPublishedAt: new Date().toISOString(),
      };
      // Commit synchronously before React renders the state update. An auth
      // resume effect or rapid second click can still hold the pre-save closure;
      // this ref prevents that stale callback from creating map two.
      replaceShare(committedShare);
      // FAIL-474: a sheet-sourced map, once persisted by a signed-in owner, is a
      // LIVE source — register the daily-sync binding on this first mint (runs
      // once; re-saves go through updateSavedSnapshot, which never re-binds).
      if (signedIn && sheetEntitlement.isPaid && source?.kind === "sheets_url" && source.url && body.dataset_id) {
        void registerSheetBinding(body.dataset_id, source.url);
      }
      return true;
    } catch (err) {
      setMintError(
        err instanceof Error
          ? err.message
          : `${intent === "save" ? "Saving" : "Publishing"} failed — try again in a moment.`,
      );
      return false;
    } finally {
      mintInFlightRef.current = false;
      setMinting(false);
    }
  }

  /**
   * Save the current map to the account (FAIL-475). First save mints the record
   * (same POST as Share); a re-save after edits PATCHes the snapshot in place —
   * no duplicate map, no create quota. Signed-out users are sent to sign in.
   *
   * FAIL-485: the draft is always established by the time this is callable
   * (pointSet/spec require a prior handleMakeMap, which calls establishDraft),
   * so draftId is real here — pass it through the redirect so the auth return
   * trip can resume it (see the resume effect above + SignUpPage/SignInPage).
   *
   * A signed-out editor is not necessarily a first-time user. Start with Sign
   * in so an existing Google identity establishes a Clerk session correctly;
   * the widget links new users to Sign up with the same draft continuation.
   */
  async function performSave(
    titleOverride?: string,
    saveSource: "automatic" | "resume" | "manual" = "manual",
  ) {
    if (!pointSet || !spec || minting) return;
    if (!signedIn) {
      const id = draftId ?? nlFallbackId;
      try {
        rememberResumeDraft(window.sessionStorage, id);
      } catch {
        // The URL continuation remains available if tab storage is blocked.
      }
      window.location.href = `/sign-in?resume=${encodeURIComponent(id)}`;
      return;
    }
    const attempt = resolveSaveAttempt({
      inFlight: saveInFlightRef.current,
      hasRenderedShare: share !== null,
      hasCommittedShare: hasCommittedShareRef.current,
    });
    if (attempt === "skip") return;

    saveInFlightRef.current = true;
    try {
      const firstSave = attempt === "create";
      const ok = firstSave
        ? await mintShare("save", titleOverride, saveSource)
        : await updateSavedSnapshot(titleOverride);
      if (ok) {
        pushToast({ variant: "success", title: firstSave ? "Saved to your maps" : "Changes saved" });
      }
    } finally {
      saveInFlightRef.current = false;
    }
  }

  async function persistMapNameForAuth(title: string, confirmed: boolean) {
    if (!analysis || !mapping || !source) return;
    await saveDraft(
      makeDraft({
        draft_id: draftId ?? nlFallbackId,
        title,
        title_confirmed: confirmed,
        source,
        profiles: analysis.profiles,
        mapping,
        rows,
        country_default: analysis.countryDefault,
        spec: nlBase ?? spec ?? undefined,
        nl_ops: nlOps,
        saved_views: savedViews,
      }),
    ).catch(() => {
      // IndexedDB unavailable — the URL continuation remains as a fallback.
    });
  }

  function openNamePrompt() {
    setNamePromptValue(mapTitle);
    setNamePromptError(null);
    setNamePromptOpen(true);
  }

  async function confirmFirstSaveName() {
    const nextName = namePromptValue.trim();
    if (nextName === "") {
      setNamePromptError("Enter a name for your map.");
      return;
    }
    setMapName(nextName);
    setInlineName(nextName);
    setMapNameConfirmed(true);
    setNamePromptOpen(false);
    setNamePromptError(null);
    await persistMapNameForAuth(nextName, true);
    await performSave(nextName);
  }

  async function handleSave(saveSource: "resume" | "manual" = "manual") {
    if (!share && !mapNameConfirmed) {
      openNamePrompt();
      return;
    }
    await performSave(undefined, saveSource);
  }

  async function commitInlineMapName() {
    const nextName = inlineName.trim();
    if (nextName === "") {
      setInlineName(mapTitle);
      setRenamingMap(false);
      return;
    }
    const changed = nextName !== mapTitle;
    setMapName(nextName);
    setInlineName(nextName);
    setRenamingMap(false);
    if (!changed) return;
    if (share) {
      const ok = await updateSavedSnapshot(nextName);
      if (ok) pushToast({ variant: "success", title: "Map renamed" });
    } else {
      await persistMapNameForAuth(nextName, mapNameConfirmed);
    }
  }

  /** PATCH the already-saved map's snapshot (owner-scoped by the session token). */
  async function updateSavedSnapshot(titleOverride?: string): Promise<boolean> {
    if (!share || !pointSet || !spec || minting) return false;
    setMinting(true);
    setMintError(null);
    try {
      const snapshot = buildSnapshot(pointSet, spec, {
        title: titleOverride ?? mapTitle,
        attribution: tile.attribution,
        pinnedView: spec.view.fit ? null : { center: spec.view.center, zoom: spec.view.zoom },
        savedViews,
      });
      const res = await fetch(`/api/maps/${share.slug}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ action: "update_snapshot", snapshot, column_schema: pointSet.columns, nl_op_stack: nlOps }),
      });
      const body = (await res.json().catch(() => null)) as {
        visibility?: ShareVisibility;
        unpublished_paid_feature?: boolean;
      } | null;
      if (!res.ok) throw new Error("Save failed — try again in a moment.");
      if (body?.visibility) {
        setShare((prev) => (prev ? { ...prev, visibility: body.visibility! } : prev));
      }
      if (body?.unpublished_paid_feature) {
        pushToast({
          variant: "info",
          title: "Saved as a draft",
          message: "Upgrade when you’re ready to publish this map with paid features.",
        });
      }
      return true;
    } catch (err) {
      setMintError(err instanceof Error ? err.message : "Save failed — try again in a moment.");
      return false;
    } finally {
      setMinting(false);
    }
  }

  function handleOpenShare() {
    setShareOpen(true);
    if (!share && !minting) void mintShare("publish");
  }

  /**
   * Upgrade for the blocked benefit (M7 FR-Q5): stash the gated action as the
   * checkout intent and redirect STRAIGHT to hosted Stripe Checkout. Always POSTs
   * to /api/billing/checkout — when `upsellPlan` didn't resolve at render time the
   * plan_id is simply omitted and the route defaults to the single paid tier
   * server-side (mirrors the shared UpgradeButton). Only a failed response or a
   * thrown request falls back to /pricing, so the button never dead-ends.
   */
  async function resumeCapturedPaywallCheckout(leadId: string) {
    setUpgrading(true);
    try {
      const origin = window.location.origin;
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          schema_version: 1,
          billing_cycle: "monthly",
          paywall_lead_id: leadId,
          success_url: `${origin}/?checkout=success`,
          cancel_url: origin,
        }),
      });
      const data = res.ok ? ((await res.json().catch(() => null)) as { url?: string } | null) : null;
      if (!data?.url) throw new Error("Could not resume checkout.");
      window.location.href = data.url;
    } catch {
      setUpgrading(false);
      pushToast({
        variant: "danger",
        title: "Checkout couldn't start",
        message: "Your map is safe. Open the upgrade prompt and try again.",
      });
    }
  }

  async function captureAnonymousPaywallLead(checkoutAttemptId: string, email: string) {
    setUpgrading(true);
    try {
      const res = await fetch("/api/billing/paywall-lead", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          email,
          trigger_event: upsell ? quotaLimitToTrigger(upsell.limit) : "unknown",
          quota_type: upsell ? quotaLimitToQuotaType(upsell.limit) : "unknown",
          plan_code: upsellPlan?.tier,
          intended_action: upsell?.action,
          draft_id: draftId ?? nlFallbackId,
          usage: upsell?.usage ?? 0,
          checkout_attempt_id: checkoutAttemptId,
        }),
      });
      const data = res.ok ? ((await res.json().catch(() => null)) as { lead_id?: string } | null) : null;
      if (!data?.lead_id) throw new Error("We couldn't save that email. Try again.");
      window.location.href = buildPaywallAuthHref({
        path: "/sign-up",
        draftId: draftId ?? nlFallbackId,
        leadId: data.lead_id,
      });
    } catch (err) {
      setUpgrading(false);
      throw err;
    }
  }

  async function startUpgrade(checkoutAttemptId = crypto.randomUUID(), email?: string) {
    if (!signedIn && upsell) {
      if (!email) throw new Error("Enter your email to continue.");
      return captureAnonymousPaywallLead(checkoutAttemptId, email);
    }
    setUpgrading(true);
    try {
      const origin = window.location.origin;
      const res = await fetch("/api/billing/checkout", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          schema_version: 1,
          ...(upsellPlan ? { plan_id: upsellPlan.tier } : {}),
          billing_cycle: "monthly",
          ...(anonSessionId ? { anon_session_id: anonSessionId } : {}),
          intended_action: upsell?.action,
          trigger_event: upsell ? quotaLimitToTrigger(upsell.limit) : undefined,
          quota_type: upsell ? quotaLimitToQuotaType(upsell.limit) : undefined,
          checkout_attempt_id: checkoutAttemptId,
          success_url: `${origin}/?checkout=success`,
          cancel_url: origin,
        }),
      });
      const data = res.ok ? ((await res.json().catch(() => null)) as { url?: string } | null) : null;
      if (res.ok && !data?.url) {
        emitCheckoutFailed(clientTrack, analyticsCtx, {
          checkoutAttemptId,
          failureCode: "invalid_checkout_response",
          planCode: upsellPlan?.tier,
          trigger: upsell ? quotaLimitToTrigger(upsell.limit) : undefined,
          quotaType: upsell ? quotaLimitToQuotaType(upsell.limit) : undefined,
          failurePhase: "client_request",
          httpStatus: res.status,
        });
      }
      window.location.href = data?.url ?? "/pricing";
    } catch {
      emitCheckoutFailed(clientTrack, analyticsCtx, {
        checkoutAttemptId,
        failureCode: "network_error",
        planCode: upsellPlan?.tier,
        trigger: upsell ? quotaLimitToTrigger(upsell.limit) : undefined,
        quotaType: upsell ? quotaLimitToQuotaType(upsell.limit) : undefined,
        failurePhase: "client_request",
      });
      window.location.href = "/pricing";
    } finally {
      setUpgrading(false);
    }
  }

  async function handleSetVisibility(visibility: ShareVisibility) {
    if (!share) return;
    const res = await fetch(`/api/maps/${share.slug}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action: "set_visibility", visibility }),
    }).catch(() => null);
    if (res?.ok) {
      setShare((prev) =>
        prev ? { ...prev, visibility, lastPublishedAt: visibility === "private" ? prev.lastPublishedAt : new Date().toISOString() } : prev,
      );
      return;
    }
    if (res?.status === 409) {
      const body = (await res.json().catch(() => null)) as {
        status?: string;
        upsell?: { limit: QuotaLimit; action: string } | null;
        usage?: number;
        copy_variant?: PaywallCopyVariant;
      } | null;
      if (body?.status === "quota_exceeded" && body.upsell) {
        setShareOpen(false);
        setUpsell({
          limit: body.upsell.limit,
          action: body.upsell.action,
          usage: body.usage ?? 0,
          copyVariant: body.copy_variant ?? "control",
        });
        return;
      }
    }
    // MAP-121/D7: the server-side private_share parity gate (unlisted/private
    // are Pro-only, mirroring the account SharePanel's MAP-118 gate). `share`
    // is never updated optimistically above, so there's nothing to revert.
    // ShareModal's own client-side gate (D7) means a Pro/free selection made
    // through the listbox never reaches here gated in the first place — this
    // 402 is only a defensive catch for a stale client, and now opens the
    // same generic Pro modal a client-side gate would have, instead of the
    // inline upgrade note the modal rendered before D7's listbox landed.
    if (res?.status === 402) {
      const body = (await res.json().catch(() => null)) as { status?: string; trigger?: string } | null;
      if (body?.status === "gated") {
        openProUpsell({
          trigger: "private_share",
          planCode: upsellPlan?.tier,
          draftId: draftId ?? nlFallbackId,
          intendedAction: "set_private_share",
        });
        return;
      }
    }
    pushToast({ variant: "danger", title: "Couldn't change visibility", message: "Try again in a moment." });
  }

  async function handleRotate() {
    if (!share) return;
    const res = await fetch(`/api/maps/${share.slug}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action: "rotate" }),
    }).catch(() => null);
    const body = res?.ok
      ? ((await res.json().catch(() => null)) as { slug?: string; url?: string; embed_url?: string } | null)
      : null;
    if (body?.slug && body.url && body.embed_url) {
      setShare((prev) =>
        prev ? { ...prev, slug: body.slug!, url: body.url!, embedUrl: body.embed_url! } : prev,
      );
      pushToast({ variant: "success", title: "Link rotated", message: "The old link is dead — share the new one." });
    } else {
      pushToast({ variant: "danger", title: "Couldn't rotate the link", message: "Try again in a moment." });
    }
  }

  async function handleRevoke() {
    if (!share) return;
    const res = await fetch(`/api/maps/${share.slug}`, { method: "DELETE" }).catch(() => null);
    if (res?.ok) {
      replaceShare(null);
      setShareOpen(false);
      pushToast({
        variant: "success",
        title: "Link revoked",
        message: "The URL and every embed now 404. Share again to mint a fresh link.",
      });
    } else {
      pushToast({ variant: "danger", title: "Couldn't revoke the link", message: "Try again in a moment." });
    }
  }

  async function handleSetViewersCanExport(value: boolean) {
    if (!share) return;
    const res = await fetch(`/api/maps/${share.slug}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ action: "set_viewers_can_export", viewers_can_export: value }),
    }).catch(() => null);
    if (res?.ok) {
      setShare((prev) => (prev ? { ...prev, viewersCanExport: value } : prev));
    } else {
      pushToast({ variant: "danger", title: "Couldn't update export setting", message: "Try again in a moment." });
    }
  }

  /* ------------------------------------------------------------- M5 · NL */

  function resetNLState() {
    setNlOps([]);
    setNlRedoOps([]);
    setNlBase(null);
    setNlHighlights([]);
    setNlAnnotations([]);
    setNlFit(null);
  }

  /**
   * Persist the current NL state (op stack + base spec + saved views) onto the
   * resumable draft. The persisted artifact is structured ops + views — never
   * the prompt or an LLM response — so a reload re-renders the fold with ZERO
   * /api/nl calls (AC-2). Best-effort; a storage failure never blocks the flow.
   */
  function persistNLState(next?: { ops?: MapOp[]; base?: MapSpec | null; views?: SavedView[] }) {
    if (!analysis || !mapping || !source) return;
    const baseForDraft = next?.base ?? nlBase ?? spec;
    void saveDraft(
      makeDraft({
        draft_id: draftId ?? nlFallbackId,
        title: mapName,
        title_confirmed: mapNameConfirmed,
        source,
        profiles: analysis.profiles,
        mapping,
        rows,
        country_default: analysis.countryDefault,
        spec: baseForDraft ?? undefined,
        nl_ops: next?.ops ?? nlOps,
        saved_views: next?.views ?? savedViews,
      }),
    ).catch(() => {
      // IndexedDB unavailable — NL still works this session, resume doesn't. Non-fatal.
    });
  }

  /** Emit the M12 mutation event for a landed NL op, tagged NL-driven. */
  function emitMapMutation(op: MapOp, via: "gui" | "nl") {
    const column = typeof op.params?.column === "string" ? op.params.column : undefined;
    if (op.type === "set_filter" || op.type === "clear_filter") {
      analytics.mapFiltered(analyticsCtx, { via, filter_field: column });
    } else {
      analytics.mapStyled(analyticsCtx, { via, column });
    }
  }

  /** The current camera as a pinnable viewport (for a saved view), or null. */
  function currentViewport(): { center: [number, number]; zoom: number } | null {
    const map = liveMapRef.current;
    if (map) {
      const c = map.getCenter();
      return { center: [c.lng, c.lat], zoom: map.getZoom() };
    }
    if (spec && spec.view.fit === false) return { center: spec.view.center, zoom: spec.view.zoom };
    return null;
  }

  function handleNLAnnotate(annotations: Array<{ id: string; text: string }>) {
    setNlAnnotations(annotations);
  }

  /** Honor a QUERY `fit` effect — bump the nonce so identical ids still re-frame. */
  function handleNLFit(ids: string[]) {
    setNlFit((prev) => ({ ids, nonce: (prev?.nonce ?? 0) + 1 }));
  }

  /** Promote a QUERY result to a persisted SavedView (AC-10) + publish it forward. */
  function handleSaveView(req: SaveViewRequest) {
    const view = buildSavedView({
      sourceQuestion: req.sourceQuestion,
      filterPredicate: req.filterPredicate,
      styleOverlay: nlOps,
      viewport: currentViewport(),
    });
    const views = [...savedViews, view];
    setSavedViews(views);
    persistNLState({ views });
    pushToast({
      variant: "success",
      title: "View saved",
      message: "It'll render on your shared and embedded map — no question re-asked.",
    });
  }

  /**
   * Manual (GUI) style edits made while NL ops exist must survive NL
   * Undo/Reset, so every changed spec region is folded into the NL base as
   * well — the base stays "the spec as the GUI authored it", and the rendered
   * spec stays the fold of the op stack over it.
   */
  function handleGuiSpecChange(next: MapSpec) {
    setNlRedoOps([]);
    setNlBase((base) => {
      if (!base || !spec) return base;
      const merged: Record<string, unknown> = { ...base };
      for (const key of Object.keys(next) as (keyof MapSpec)[]) {
        if (JSON.stringify(next[key]) !== JSON.stringify(spec[key])) merged[key] = next[key];
      }
      return merged as unknown as MapSpec;
    });
    setSpec(next);
  }

  /** NL STYLE ops landed: remember the base beneath the stack, render the fold. */
  function handleNLStyle(result: NLApplyResult, baseUsed: MapSpec) {
    const base = nlOps.length === 0 ? baseUsed : nlBase ?? baseUsed;
    if (nlOps.length === 0) setNlBase(baseUsed);
    // The just-landed ops (tagged NL-driven for the M12 style/filter funnel).
    for (const op of result.ops.slice(nlOps.length)) emitMapMutation(op, "nl");
    setNlOps(result.ops);
    setNlRedoOps([]);
    setSpec(result.spec);
    nlAppliedAt.current = typeof performance !== "undefined" ? performance.now() : 0;
    persistNLState({ ops: result.ops, base });
  }

  /** Undo — pop the last NL op and re-reduce over the base (pure, LLM-free). */
  function handleNLUndo() {
    if (nlOps.length === 0) return;
    const base = nlBase ?? spec;
    if (!base) return;
    // Undo-within-10s is the misinterpretation-rate signal (M5 metric).
    const nowMs = typeof performance !== "undefined" ? performance.now() : 0;
    const msSinceApply = nlAppliedAt.current > 0 ? Math.round(nowMs - nlAppliedAt.current) : 0;
    analytics.nlQueryUndone(analyticsCtx, {
      within_10s: msSinceApply > 0 && msSinceApply <= 10_000,
      ms_since_apply: msSinceApply,
    });
    const removed = nlOps[nlOps.length - 1];
    const ops = nlOps.slice(0, -1);
    setNlOps(ops);
    if (removed) setNlRedoOps((redoOps) => [removed, ...redoOps]);
    setSpec(reduceStack(base, ops));
    persistNLState({ ops, base });
  }

  /** Redo — append the next undone op and re-reduce locally, with no model call. */
  function handleNLRedo() {
    if (nlRedoOps.length === 0) return;
    const base = nlBase ?? spec;
    if (!base) return;
    const result = redoNL(base, nlOps, nlRedoOps);
    setNlOps(result.ops);
    setNlRedoOps(result.redoOps);
    setSpec(result.spec);
    persistNLState({ ops: result.ops, base });
  }

  /** Reset — clear the NL stack entirely: back to the GUI-authored base spec. */
  function handleNLReset() {
    const base = nlBase ?? spec;
    if (nlBase) setSpec(nlBase);
    resetNLState();
    persistNLState({ ops: [], base });
  }

  /* ------------------------------------------------------- M8 · analysis */

  // The panel hands a draw request up; the editor owns the canvas seam + spec.
  const [analysisDraw, setAnalysisDraw] = useState<"radius" | "lasso" | null>(null);
  const pendingDrawRef = useRef<AnalysisDrawRequest | null>(null);
  const analysisPresent = useMemo(() => anyToolPresent(analysisTools), [analysisTools]);

  /* -------------------------------------------------------- M9 · overlay */

  // The overlay panel is present only when the build-go flag AND a deploy-time
  // render engine AND entitlement pass. Co-gating on `overlayEngine` means an
  // enabled flag without a bound engine hides the panel (no spec-persisting,
  // render-blank overlays) instead of authoring an overlay nothing can draw.
  const overlayPresent = useMemo(
    () => overlayGate(demographicsOverlays, overlayEntitlement, overlayEngine !== undefined).present,
    [demographicsOverlays, overlayEntitlement, overlayEngine],
  );
  const overlayCatalogGroups = useMemo(() => overlayCatalog(), []);
  const [overlayData, setOverlayData] = useState<OverlayRenderData | null>(null);
  const [selectedBoundaryId, setSelectedBoundaryId] = useState<string | null>(null);
  const overlayBlock = spec?.overlay ?? null;
  const overlayKey = overlayBlock ? JSON.stringify(overlayBlock) : "";

  // Resolve the choropleth from the persisted `overlay` block via the engine —
  // the SAME resolver the M6 embed uses, so editor + embed redraw identically.
  // No engine (the wedge) → null → the choropleth layer stays inert.
  useEffect(() => {
    let cancelled = false;
    const resolve =
      overlayEngine && overlayBlock
        ? resolveOverlayData(overlayEngine, overlayBlock, getMapTokens(themeName))
        : Promise.resolve(null);
    resolve
      .then((data) => {
        if (!cancelled) setOverlayData(data);
      })
      .catch(() => {
        if (!cancelled) setOverlayData(null);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayEngine, overlayKey, themeName]);

  // Geo-join the user's OWN rows to the resolved boundaries (client-side, from
  // the in-memory PointSet) so a boundary click can show its aggregate.
  const overlayRollups = useMemo(() => {
    if (!pointSet || !overlayData || !overlayBlock) return null;
    const numericColumns = pointSet.columns.filter((c) => c.type === "number").map((c) => c.name);
    return geoJoin(pointSet.points, overlayData.boundaries, overlayBlock.tier, { columns: numericColumns });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pointSet, overlayData, overlayBlock?.tier]);
  const selectedRollup = useMemo(
    () => overlayRollups?.boundaries.find((b) => b.boundaryId === selectedBoundaryId) ?? null,
    [overlayRollups, selectedBoundaryId],
  );

  function handleBeginDraw(req: AnalysisDrawRequest) {
    pendingDrawRef.current = req;
    setAnalysisDraw(req.kind);
  }
  function handleCancelDraw() {
    pendingDrawRef.current = null;
    setAnalysisDraw(null);
  }

  /** A draw gesture completed on the canvas → build the artifact, fold into the spec. */
  function handleAnalysisDraw(
    result:
      | { kind: "lasso"; ring: [number, number][] }
      | { kind: "radius"; center: [number, number]; radiusMeters: number },
  ) {
    const req = pendingDrawRef.current;
    pendingDrawRef.current = null;
    setAnalysisDraw(null);
    if (!pointSet || !spec) return;
    const current = spec.analysis ?? emptyAnalysis();
    if (result.kind === "radius") {
      const circle = buildRadiusCircle(pointSet, result.center, result.radiusMeters);
      handleGuiSpecChange({ ...spec, analysis: { ...current, radii: [...(current.radii ?? []), circle] } });
      return;
    }
    const count = current.territories?.items.length ?? 0;
    const name = req?.kind === "lasso" ? req.name : `Territory ${count + 1}`;
    const color = req?.kind === "lasso" ? req.color : territoryColor(getMapTokens(themeName).palette, count);
    const territory = buildLassoTerritory(pointSet, result.ring, { name, color });
    const items = [...(current.territories?.items ?? []), territory];
    handleGuiSpecChange({
      ...spec,
      analysis: {
        ...current,
        territories: { mode: current.territories?.mode ?? "lasso", items, balanceBy: current.territories?.balanceBy },
      },
    });
  }

  const hasChanges = analysis !== null && mapping !== analysis.mapping;
  const live = pointSet !== null && spec !== null;

  const matchContent = (idPrefix: string, embedded = false) =>
    analysis && mapping ? (
      <MatchPanel
        profiles={analysis.profiles}
        mapping={mapping}
        overridden={overridden}
        hasChanges={hasChanges}
        summary={effectiveSummary}
        rowCeiling={analysis.rowCeiling}
        idPrefix={idPrefix}
        embedded={embedded}
        busy={geocoding}
        live={live}
        onModeChange={handleModeChange}
        onRoleChange={handleRoleChange}
        onToggleLabel={handleToggleLabel}
        onReset={handleReset}
        onMakeMap={() => void handleMakeMap()}
      />
    ) : null;

  /**
   * Side-panel content. Before the first render it's the match panel alone;
   * once the map is live, Style takes the front tab and the match/attention
   * stack moves behind "Data" — one column, never panels-inside-panels.
   */
  const panelContent = (idPrefix: string, embedded = false) =>
    live && pointSet && spec ? (
      <Tabs
        defaultValue="style"
        items={[
          {
            id: "style",
            label: "Style",
            content: (
              <Card>
                <StylePanel
                  pointSet={pointSet}
                  spec={spec}
                  themeName={themeName}
                  idPrefix={idPrefix}
                  onSpecChange={handleGuiSpecChange}
                />
              </Card>
            ),
          },
          ...(AI_ASSIST_ENABLED ? [{
            id: "ai",
            label: "AI",
            content: aiAssistAllowed ? (
              <NLBar
                mapId={draftId ?? nlFallbackId}
                brandName={brandName}
                anonSessionId={anonSessionId}
                initialInput={promptSeed}
                pointSet={pointSet}
                baseSpec={nlBase ?? spec}
                ops={nlOps}
                onStyle={handleNLStyle}
                onHighlight={setNlHighlights}
                onAnnotate={handleNLAnnotate}
                onFit={handleNLFit}
                onSaveView={handleSaveView}
                onUndo={handleNLUndo}
                canRedo={nlRedoOps.length > 0}
                onRedo={handleNLRedo}
                onReset={handleNLReset}
              />
            ) : (
              <AiLockedCard onUpgrade={() => openProUpsell({ trigger: "ask_map", planCode: "pro" })} />
            ),
          }] : []),
          // MAP-102 — the basemap picker, lifted out of the Style dropdown into
          // its own scannable tile grid. Satellite is available for exploration
          // and private drafts; Lite entitlement is checked at publish.
          {
            id: "maps",
            label: "Maps",
            content: (
              <Card>
                <BasemapPanel
                  spec={spec}
                  isPaid={basemapEntitlement.isPaid}
                  onSpecChange={handleGuiSpecChange}
                />
              </Card>
            ),
          },
          // MAP-7 — add a place by address / remove an individual pin. Present
          // whenever the map is live, for both freshly ingested and rehydrated
          // (saved) maps (a rehydrated map has no ingest grid, but the PointSet
          // is the dataset either way).
          {
            id: "places",
            label: "Places",
            content: (
              <PlacesPanel
                points={pointSet.points}
                titleColumn={spec.popup.titleColumn}
                atCeiling={pointSet.count >= HARD_ROW_CEILING}
                idPrefix={idPrefix}
                embedded={embedded}
                onFind={handleFindPlace}
                onAdd={handleAddPlace}
                onRemove={handleRemovePlace}
              />
            ),
          },
          ...(analysisPresent
            ? [
                {
                  id: "analysis",
                  label: "Analysis",
                  content: (
                    <Card>
                      <AnalysisPanel
                        pointSet={pointSet}
                        spec={spec}
                        themeName={themeName}
                        idPrefix={idPrefix}
                        brandName={brandName}
                        toolFlags={analysisTools}
                        entitlement={analysisEntitlement}
                        drawMode={analysisDraw}
                        onSpecChange={handleGuiSpecChange}
                        onBeginDraw={handleBeginDraw}
                        onCancelDraw={handleCancelDraw}
                      />
                    </Card>
                  ),
                },
              ]
            : []),
          ...(overlayPresent
            ? [
                {
                  id: "overlay",
                  label: "Census",
                  content: (
                    <Card>
                      <OverlayPanel
                        spec={spec}
                        themeName={themeName}
                        idPrefix={idPrefix}
                        brandName={brandName}
                        demographicsOverlays={demographicsOverlays}
                        engineAvailable={overlayEngine !== undefined}
                        entitlement={overlayEntitlement}
                        catalog={overlayCatalogGroups}
                        onSpecChange={handleGuiSpecChange}
                        choropleth={overlayData?.choropleth ?? null}
                        rollup={selectedRollup}
                      />
                    </Card>
                  ),
                },
              ]
            : []),
          // The "Data" tab is the ingest match panel (columns/rows). A rehydrated
          // map has no ingest rows to show, so it's omitted there.
          ...(analysis ? [{ id: "data", label: "Data", content: matchContent(idPrefix, embedded) }] : []),
        ]}
      />
    ) : (
      matchContent(idPrefix, embedded)
    );

  return (
    <AppShell>
      {/* `live` covers the rehydrated-map case (a saved map opened via ?map=<id>
          has no ingest analysis/mapping, but pointSet+spec make it live). */}
      {(analysis && mapping) || live ? (
      <Topbar
        start={<BrandLockup brandName={brandName} wordmark={wordmark} href={signedIn ? "/my-maps/" : "/"} />}
        end={
          <>
            {analysis && source && live && !signedIn ? (
              <Link className="pz-sitenav__link" href="/sign-in">Sign in to save</Link>
            ) : null}
            {/* The shared auth-aware nav (account menu + Sign out when signed in),
                so the editor no longer drops the account controls mid-session. */}
            <AppNav signedIn={signedIn} isPaid={analysisEntitlement.isPaid} />
          </>
        }
      />
      ) : null}
      <ShellMain>
        {(analysis && mapping) || live ? (
          <>
          {signedIn ? (
            <Breadcrumb
              items={
                initialMap
                  ? [{ label: "My maps", href: "/my-maps/" }, { label: mapTitle }]
                  : [{ label: "My maps", href: "/my-maps/" }]
              }
            />
          ) : null}
          <div className="ingest-work">
            {(analysis && source) || live ? (
              <div className="ingest-toolbar">
                {renamingMap ? (
                  <form
                    className="ingest-map-name ingest-map-name--editing"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void commitInlineMapName();
                    }}
                  >
                    <Input
                      value={inlineName}
                      aria-label="Map name"
                      maxLength={300}
                      onChange={(event) => setInlineName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          setInlineName(mapTitle);
                          setRenamingMap(false);
                        }
                      }}
                    />
                    <Button type="submit" size="sm">
                      Save
                    </Button>
                  </form>
                ) : (
                  <div className="ingest-map-name">
                    <h1 className="ingest-map-name__title">{mapTitle}</h1>
                    <IconButton
                      label={`Rename map: ${mapTitle}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setInlineName(mapTitle);
                        setRenamingMap(true);
                      }}
                    >
                      <Icon icon={Pencil} size="sm" />
                    </IconButton>
                  </div>
                )}
                <div className="ingest-toolbar__actions">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="ingest-panel-toggle"
                    iconLeading={<Icon icon={SlidersHorizontal} size="sm" />}
                    onClick={() => setPanelOpen(true)}
                  >
                    {live ? "Style" : "Match"}
                  </Button>
                  {live && (
                    <>
                      <Button
                        variant="secondary"
                        size="sm"
                        iconLeading={<Icon icon={Share2} size="sm" />}
                        onClick={handleOpenShare}
                      >
                        Share
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        loading={minting}
                        iconLeading={<Icon icon={Save} size="sm" />}
                        onClick={() => void handleSave()}
                      >
                        Save
                      </Button>
                    </>
                  )}
                  <div className="ingest-overflow" ref={overflowRef}>
                    <IconButton
                      label="More map actions"
                      size="sm"
                      className="ingest-overflow__trigger"
                      aria-haspopup="menu"
                      aria-expanded={overflowOpen}
                      onClick={() => setOverflowOpen((open) => !open)}
                    >
                      <Icon icon={Ellipsis} size="sm" />
                    </IconButton>
                    {overflowOpen ? (
                      <div className="ingest-overflow__menu" role="menu">
                        {live ? (
                          <button
                            type="button"
                            role="menuitem"
                            className="ingest-overflow__item"
                            onClick={() => {
                              setExportOpen(true);
                              setOverflowOpen(false);
                            }}
                          >
                            <Icon icon={Download} size="sm" />
                            Export
                          </button>
                        ) : null}
                        <button
                          type="button"
                          role="menuitem"
                          className="ingest-overflow__item ingest-overflow__item--danger"
                          onClick={() => {
                            setStartOverOpen(true);
                            setOverflowOpen(false);
                          }}
                        >
                          <Icon icon={RotateCcw} size="sm" />
                          Start over
                        </button>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            <MapPlate
              mode={mapping?.mode ?? "coordinate"}
              ready={effectiveSummary.ready}
              setAside={rows.length - effectiveSummary.ready - effectiveSummary.skipped}
              skipped={effectiveSummary.skipped}
              geocoding={geocoding}
              placementFailed={placementFailed}
              rows={rows}
              excluded={excluded}
              rowCeiling={analysis?.rowCeiling ?? { level: "ok" }}
              onRemoveBrokenRow={handleRemoveBrokenRow}
              mapping={mapping!}
              onRemediateRow={handleRemediateRow}
              live={live && pointSet && spec ? { pointSet, spec } : null}
              preview={!live ? mappingPreview : null}
              tile={tile}
              satelliteTile={satelliteTile}
              themeName={themeName}
              onViewChange={handleViewChange}
              highlightIds={nlHighlights}
              annotations={nlAnnotations}
              fitRequest={nlFit}
              analysisDraw={analysisDraw}
              onAnalysisDraw={handleAnalysisDraw}
              overlayData={overlayData}
              onBoundaryClick={setSelectedBoundaryId}
              onMapReady={(map) => {
                liveMapRef.current = map;
              }}
            />
            <aside className="ingest-side" aria-label={live ? "Map controls" : "Match panel"}>
              {panelContent("side")}
            </aside>
          </div>
          </>
        ) : appMode ? (
          /* Signed-in create surface (FAIL-458) — the SAME real intake, minus
             the marketing landing: a clean in-app "start a new map" panel under
             the APP bar (MAP-107 follow-up: this branch kept the marketing
             pill-nav SiteHeader after the A3 swap; it must match the working
             editor's Topbar above). A file drop / paste / sheet flips
             `analysis` and the working branch above takes over, so /maps/new
             becomes the editor in place. */
          <>
            <Topbar
              start={<BrandLockup brandName={brandName} wordmark={wordmark} href={signedIn ? "/my-maps/" : "/"} />}
              end={<AppNav signedIn={signedIn} isPaid={analysisEntitlement.isPaid} />}
            />
            <div className="ingest-appnew">
              {appIntakeHeader}
              <IntakeSurface
                variant="hero"
                presentation="equal-cards"
                loading={loading}
                error={intakeError}
                initialMethod={initialIntake}
                pasteSlot={<PastePanel loading={loading === "paste"} onSubmit={handlePaste} />}
                sheetSlot={
                  sheetEntitlement.isPaid ? (
                    <SheetPanel
                      onSubmit={handleSheetCsv}
                      onFetchStarted={() =>
                        analytics.sheetFetchStarted(analyticsCtx, { intake_surface: intakeSurface })
                      }
                      onFetchSucceeded={(rowCount) =>
                        analytics.sheetFetchSucceeded(analyticsCtx, {
                          intake_surface: intakeSurface,
                          row_count: rowCount,
                        })
                      }
                      onFetchFailed={(failureCode) =>
                        analytics.sheetFetchFailed(analyticsCtx, {
                          intake_surface: intakeSurface,
                          failure_code: failureCode,
                        })
                      }
                    />
                  ) : (
                    <SheetLockedCard />
                  )
                }
                onFile={handleFile}
                onPaste={handlePaste}
                onSheets={handleSheets}
                onMethodOpened={(method) =>
                  analytics.intakeMethodOpened(analyticsCtx, { intake_surface: intakeSurface, method })
                }
                onFilePickerOpened={() =>
                  analytics.intakeFilePickerOpened(analyticsCtx, { intake_surface: intakeSurface })
                }
              />
              <p className="ingest-appnew__sample">
                <a className="ingest-appnew__sample-link" href="/sample-map-data.csv" download>
                  <Icon icon={Download} size="sm" />
                  Download a sample spreadsheet to get started.
                </a>
              </p>
            </div>
            <SiteFooter
              brandName={brandName}
              wordmark={wordmark}
              attribution={tile.attribution}
              legal={landingLegal}
              signedIn={signedIn}
              markUrl={markUrl}
            />
          </>
        ) : (
          /* No data yet — "/" IS the modern-warm landing. The hero hosts the
             REAL intake (same handlers as ever); a file drop / paste / sheet
             flips `analysis` and the branch above takes over unchanged. The
             pill-nav sits ABOVE .landing (sticky, full-page) since .landing and
             .landing-hero clip/overflow would otherwise bound the sticky. */
          <>
            <SiteHeader brandName={brandName} wordmark={wordmark} signedIn={signedIn} />
            <div className="landing">
            <LandingHero
              intake={
                <IntakeSurface
                  variant="hero"
                  presentation="equal-cards"
                  loading={loading}
                  error={intakeError}
                  initialMethod={initialIntake}
                  fileInputRef={landingFileInputRef}
                  pasteSlot={<PastePanel loading={loading === "paste"} onSubmit={handlePaste} />}
                  sheetSlot={
                    <SheetPanel
                      onSubmit={handleSheetCsv}
                      onFetchStarted={() =>
                        analytics.sheetFetchStarted(analyticsCtx, { intake_surface: intakeSurface })
                      }
                      onFetchSucceeded={(rowCount) =>
                        analytics.sheetFetchSucceeded(analyticsCtx, {
                          intake_surface: intakeSurface,
                          row_count: rowCount,
                        })
                      }
                      onFetchFailed={(failureCode) =>
                        analytics.sheetFetchFailed(analyticsCtx, {
                          intake_surface: intakeSurface,
                          failure_code: failureCode,
                        })
                      }
                    />
                  }
                  onFile={handleFile}
                  onPaste={handlePaste}
                  onSheets={handleSheets}
                  onMethodOpened={(method) =>
                    analytics.intakeMethodOpened(analyticsCtx, { intake_surface: intakeSurface, method })
                  }
                  onFilePickerOpened={() =>
                    analytics.intakeFilePickerOpened(analyticsCtx, { intake_surface: intakeSurface })
                  }
                />
              }
            />
            <LandingSteps />
            {/* AI/NL "Ask your map" promo hidden for launch (AI_ASSIST_ENABLED, MAP-135). */}
            {AI_ASSIST_ENABLED && <LandingAsk />}
            <LandingSync brandName={brandName} onChooseFile={() => landingFileInputRef.current?.click()} />
            <LandingApi apiBaseUrl={apiBaseUrl} />
            <LandingCta />
            <SiteFooter
              brandName={brandName}
              wordmark={wordmark}
              attribution={tile.attribution}
              legal={landingLegal}
              signedIn={signedIn}
              markUrl={markUrl}
            />
            </div>
          </>
        )}
      </ShellMain>
      {analysis && mapping && (
        <Drawer
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          side="right"
          scrim
          title={live ? "Style your map" : "Match your columns"}
          className="ingest-drawer"
        >
          <div className="ingest-drawer-stack">{panelContent("drawer", true)}</div>
        </Drawer>
      )}
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        brandName={brandName}
        mapTitle={mapTitle}
        signedIn={signedIn}
        share={share}
        minting={minting}
        mintError={mintError}
        onRetry={() => void mintShare("publish")}
        onSetVisibility={handleSetVisibility}
        isPaid={analysisEntitlement.isPaid}
        onRotate={handleRotate}
        onRevoke={handleRevoke}
        viewersCanExport={share?.viewersCanExport ?? false}
        onSetViewersCanExport={handleSetViewersCanExport}
        onSnippetCopy={() =>
          share && analytics.embedSnippetCopied(analyticsCtx, { map_id: share.slug, embed_mode: "standard" })
        }
      />
      {upsell && (
        <Paywall
          open
          onClose={() => setUpsell(null)}
          limit={upsell.limit}
          brandName={brandName}
          logoUrl={markUrl}
          price={upsellPlan?.price ?? null}
          planCode={upsellPlan?.tier ?? ""}
          planMapPointLimit={upsellPlan?.mapPointLimit}
          usage={upsell.usage}
          copyVariant={upsell.copyVariant}
          anonSessionId={anonSessionId}
          requireEmail={!signedIn}
          signInHref={buildPaywallAuthHref({
            path: "/sign-in",
            draftId: draftId ?? nlFallbackId,
          })}
          onCheckout={(checkoutAttemptId, email) => startUpgrade(checkoutAttemptId, email)}
          upgrading={upgrading}
        />
      )}
      {frozenMap && (
        <div className="pz-frozen-overlay">
          <FrozenMapNotice
            mapTitle={frozenMap.title}
            onResubscribe={() => void startUpgrade()}
            resubscribing={upgrading}
          />
        </div>
      )}
      {pointSet && spec && (
        <ExportDialog
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          pointSet={pointSet}
          spec={spec}
          title={mapTitle}
          attribution={tile.attribution}
          getMap={() => liveMapRef.current}
          onError={(message) => pushToast({ variant: "danger", title: "Export failed", message })}
          dataExportAllowed={dataExportAllowed}
          onDataExportLocked={() => {
            setExportOpen(false);
            openProUpsell({
              trigger: "data_export",
              planCode: upsellPlan?.tier,
              draftId: draftId ?? nlFallbackId,
              intendedAction: "export_data",
            });
          }}
        />
      )}
      <Modal
        open={namePromptOpen}
        onClose={() => {
          setNamePromptOpen(false);
          setNamePromptError(null);
        }}
        title="Give your map a name and save it."
        className="ingest-name-modal"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setNamePromptOpen(false);
                setNamePromptError(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="primary" loading={minting} onClick={() => void confirmFirstSaveName()}>
              Save map
            </Button>
          </>
        }
      >
        <div className="ingest-name-field">
          <Label htmlFor="first-save-map-name">Map name</Label>
          <Input
            id="first-save-map-name"
            value={namePromptValue}
            maxLength={300}
            onChange={(event) => {
              setNamePromptValue(event.target.value);
              if (namePromptError) setNamePromptError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") void confirmFirstSaveName();
            }}
          />
          {namePromptError ? (
            <p className="ingest-name-field__error" role="alert">
              {namePromptError}
            </p>
          ) : null}
        </div>
      </Modal>
      <Modal
        open={startOverOpen}
        onClose={() => setStartOverOpen(false)}
        title="Start over?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setStartOverOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                handleStartOver();
                setStartOverOpen(false);
              }}
            >
              Start over
            </Button>
          </>
        }
      >
        <p>Starting over clears the current map and everything you’ve built in it. This can’t be undone.</p>
      </Modal>
      <ToastRegion>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            variant={t.variant}
            title={t.title}
            message={t.message}
            action={t.action}
            onDismiss={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastRegion>
    </AppShell>
  );
}
